import {
    fabric,
    IEditorCanvas,
    IEditorElement,
    IEditorFont,
    IEditorFontService,
    IEditorImage,
    IEditorPage,
    IEditorShape,
    IFreeObject,
    IMaskPreviewData,
    INonPrintableBackgroundPreviewData,
    IPage,
    IPageEdits,
    IPageList,
    IProjectResult,
    ITextEdits,
    SF,
    storefrontAnalytics,
} from './interfaces';
import {
    IEditorOptions,
    IFreeObjectAndData,
    ITextError,
    IThumbList,
    TextChangeType
} from './editor-interfaces';
import {IEditorText} from '@/editor/base/editor.interfaces';
import { CustomizerJsonApi } from './customizer-json-api';
import { ColorUtils, CustomizerImagingUtils } from './helpers';
// @ts-ignore
import photoZoneTemplate from '@/templates-compiled/photo-zone-template.js';
// @ts-ignore
import textZoneTemplate from '@/templates-compiled/text-zone-template.js';
// @ts-ignore
import pageTabsTemplate from '@/templates-compiled/page-tabs-template.js';

import {EditorController} from "./editor-controller";
import {Renderer} from "./renderer";
import {FontService} from "./font-service";
import cloneDeep from "lodash.clonedeep";
import {Ite} from "@web/ite-typescript-library";
import {ProductPreviewer} from "@/previewer/product-preview";
import ExifReader, { Tags } from 'exifreader';




export class PreviewType {
    static readonly Photo = 0;
}


const ZOOM_DELTA = 1.025;
const MAX_IMAGE_SIZE = 32767 * .95;
const SHOW_PREVIEW_ALWAYS = 1;
const SHOW_PREVIEW_ON_MOBILE = 2;

const isTouchOnly = 'ontouchstart' in window || 'ontouchstart' in document || navigator.maxTouchPoints > 0;
const clickEvent = ['touchend', 'click'];
const mouseStartEvent = ['touchstart', 'mousedown'];
const mouseDownEvent = ['touchend', 'mousedown'];

export class CustomizerEditor {
    private readonly fontService: IEditorFontService;
    private readonly editorController: EditorController;
    private readonly productImageContainer: HTMLDivElement;
    private readonly resetButton: HTMLButtonElement;
    private readonly saveButton: HTMLButtonElement;
    private readonly termsAndConditionsFileUpload: HTMLInputElement;
    private readonly spinner: HTMLDivElement;
    private readonly isMultiPage: boolean;                    // Whether there are multiple real pages, not "virtual pages"
    private readonly pages: IPage[];
    private readonly editUpload: HTMLDivElement;

    private readonly largePreviewButton: HTMLButtonElement | null;
    private readonly maxImageSize: number = 100 * 1024 * 1024;
    private editorPage: IEditorPage[] = [];
    private productImageWidth: number;              //
    private canvasContainerLeft: number;            // The left position of the canvas, used in two Sided products to move the canvas
    private editPageContent: HTMLDivElement;        // Used to contain all the UI like text and photo controls.
    private currentVisiblePage: number;             // Current page being edited, note: not a real page - only used for Two Sided Projects
    private currentPage: number;                    // Actual page, not a virtual page
    private loaded: boolean = false;                // Set to true once the editor has finished loading the project
    private previewer: ProductPreviewer;             // 3D Preview Object
    private currentFreeObjectId: number;            // The current freeObject being edited
    private currentZone: IEditorElement; // The current Zone being edited
    private cancelButtonCallback: () => void;       // Used in the prompt to removeEventListener...
    private buttonCallback: (() => void)[] = [];    // All the callbacks (used to removeEventListeners)
    private previewModal: HTMLDivElement;           // The element where the 3D Preview is shown...
    private previewSpinner: HTMLDivElement;         // The spinner that shows when the preview is loading...
    private promptModal: HTMLDivElement;            // The element
    private previewContainerEl: HTMLElement;        // The preview container...
    private edits: IPageEdits;                      // The current edits of the page
    private isDirtyPage: boolean[] = [];            // Whether each page has been edited
    private pageDirtyBeforeImage: boolean = false;  // Used when cancel is pressed on a low-quality image if the page hasn't been edited.
    private uniqueFreeObjectId: number = 0;         // Used a counter to generate unique free object ids accross pages..
    private zoomMax: number;                        // Current ZoomMax so that we don't zoom too much.
    private zoom: number;                           // Current zones zoom
    private isZooming: boolean;                     // Whether we are currently zooming (zoom is handled by mousedown and mouseup)
    private analyticsDebounceTimer: number;         // Used so we do not do too much analytics at once...
    private hasChanged: { [freeObjectId: string]: boolean } = {};
    private freeObjectToFilename: { [freeObjectId: number]: string } = {};
    private closePreviewResolve?: () => void;

    public orderComplete?: (success: boolean, previewUrl?: string, cartItemId?: number) => void;

    constructor(
        private jsonApi: CustomizerJsonApi,
        private sessionId: string,
        private projectId: number,
        pageList: IPageList[],
        private isTwoSidedProduct: boolean,
        private threeDModel: string,
        private threeDColor: string,
        private cartPreview: boolean,
        private previewKey: string,
        maskPreviewData: IMaskPreviewData,
        private threeDOverlay: { url?: string, blend?: GlobalCompositeOperation },
        nonPrintableBackgroundPreviewData: INonPrintableBackgroundPreviewData,
        private itePreview: string,
        private itePagePreviews:string[],
        private iteUrl: string,
        warningErrorDetails: { error_zoom: number, warning_zoom: number },
        private previewOutputFormat: string,
        private productId: number,
        private quantity: number,
        private forceTextChange: boolean,
        private showPreviewInForceModal: boolean,
        private alwaysShowTermsAndConditions: boolean,
        private previewOnSave: number,
        enableCanvasMargin: boolean,
        alreadyEdited: boolean
    ) {

        // The image size
        this.productImageContainer = document.querySelector<HTMLDivElement>('#product-image-container');
        this.editUpload = document.querySelector<HTMLDivElement>('#file-upload');
        this.termsAndConditionsFileUpload = document.querySelector<HTMLInputElement>('#file-upload-terms-and-conditions');
        this.spinner = document.querySelector<HTMLDivElement>('#spinner');

        // Give each field a unique id that can be used to tie to the various fields...
        for (let i = 0; i < pageList.length; i++) {
            this.fixPageForFreeObjects(pageList[i], alreadyEdited);
            this.isDirtyPage[i] = false;
        }

        if (pageList.length > 1) {
            const pageTabsHtml = pageTabsTemplate({
                pageNames: pageList.map(page => page.edits.pageName)
            });
            const pageTabs = document.querySelector<HTMLElement>('#page-tabs');
            if (pageTabs) {
                pageTabs.innerHTML = pageTabsHtml;
                pageTabs.style.display = 'block';
            }
        }

        this.currentPage = 0;
        this.isMultiPage = pageList.length > 1;
        this.edits = pageList[this.currentPage].edits;

        this.previewModal = document.getElementById('preview-modal') as HTMLDivElement;
        this.previewSpinner = document.getElementById('preview-spinner') as HTMLDivElement;
        this.promptModal = document.getElementById('prompt-modal') as HTMLDivElement;
        this.previewContainerEl = document.getElementById('preview-container');

        this.productImageWidth = this.productImageContainer.offsetWidth;
        this.fontService = new FontService(this.jsonApi);
        this.maxImageSize = SF.maxImageSize || this.maxImageSize;

        SF.Editor.Options = SF.Editor.Options || {};
        SF.Editor.Options.EmptyImageFill = '#707070';
        if ("Cache" in SF.Util) {
            SF.Util.Cache.maxFullSizeImage = 10000;
        }

        const options: IEditorOptions = {
            allowTouchScrolling: fabric.isTouchSupported,
            enableMobilePointerEvents: false,
            drawControlsOnPhoto: true,
            maxWidth: this.productImageWidth * (isTwoSidedProduct ? 2 : 1),
            maxHeight: this.productImageContainer.offsetHeight,
            panIcon: SF.staticUrlRoot + 'images/editor/icons/icon_hand_open.png',
            resolutionWarningIcon: SF.staticUrlRoot + 'images/editor/icons/resolution_alert.png',
            resolutionErrorIcon: SF.staticUrlRoot + 'images/editor/icons/resolution_error.png',
            zoomError: warningErrorDetails.error_zoom,
            zoomWarning: warningErrorDetails.warning_zoom,
            enableWheel: !fabric.isTouchSupported,
            ghostingEnabled: true,
            enableFileDrag: !fabric.isTouchSupported,
            drawInnerRotate: !fabric.isTouchSupported,
            drawHandIcon: true,
            drawGrabHandle: false,
            drawBorders: true,
            textHasInnerRotate: false,
            textHasControls: false,
            backgroundHasDragControl: false
        };

        this.pages = this.getPages(pageList);

        this.editorController = new EditorController([this.pages[this.currentPage]], this.productImageContainer, this.fontService,
            options, 400, '', '', false, undefined, undefined,
            maskPreviewData, nonPrintableBackgroundPreviewData, enableCanvasMargin);

        // Two-sided products are things like Two Sided Pillows.  Any product that is a single production image but
        // is two sides in the real world are these twoSidedProducts
        if (isTwoSidedProduct) {
            this.resizeImageContainerForTwoSidedProducts();
        }

        this.editorController.getFabricCanvas().selection = false;

        for (let i = 0; i < this.pages.length; i++) {
            this.editorPage[i] = Renderer.toPage(this.pages[i], undefined);
        }

        this.resetButton = document.getElementById('button-undo') as HTMLButtonElement;
        this.saveButton = document.getElementById('save-button') as HTMLButtonElement;
        this.largePreviewButton = document.getElementById('large-preview-button') as HTMLButtonElement | null;

        // Hide terms and conditions if there are no photos, or if the photos are already filled in...
        const edits = pageList[this.currentPage].edits;
        if (this.isReEditOrTextOnly(pageList)) {
            this.hideTermsShowControls();
        } else {
            document.getElementById('terms-and-conditions').style.visibility = 'visible';
            this.editUpload.classList.add('hidden');
            setTimeout(() => {
                document.querySelector<HTMLDivElement>('#terms-and-conditions-background').classList.add('animate');
            }, 1000);
        }
        document.querySelector<HTMLDivElement>('#main-container').classList.add('visible');

        // Single image templates (basically a template where the image is only thing on it) get a file picker as soon
        // as terms and conditions is clicked upon.
        const notSingleImageTemplate = this.isMultiPage || (pageList.length === 1 &&
            (edits.freeObject.length !== 1 || !CustomizerImagingUtils.isPhotoZone(edits.freeObject[0])));

        if (notSingleImageTemplate && this.termsAndConditionsFileUpload) {
            this.termsAndConditionsFileUpload.style.display = 'none';
        } else if (this.termsAndConditionsFileUpload) {
            if ((this.edits.freeObject.length === 1 || CustomizerImagingUtils.isPhotoZone(this.edits.freeObject[0])) && this.termsAndConditionsFileUpload) {
                this.termsAndConditionsFileUpload.dataset.freeObjectId = this.edits.freeObject[0].freeObjectId.toString();
            }
        }

        // Sets up the events for the editor controller (these are messages usually coming back from the fabric editor).
        this.addControllerEventListeners();

        // Setup UI Event Listeners
        this.addEventListeners();

        // Wait until the font is loaded before setting up the editor panels...
        this.fontService.fontsLoaded().then(() => {
            this.setupEditorPanels();
        }, err => {
            console.error('Error loading fonts', err);
            this.addAnalyticsEvent('error', 'loadingFonts', 'error', err);
        });

        // If the users resizes the browser we have to clean up some stuff...
        this.enableResizeCanvasOnWindowSizeChange();

    }

    private addAnalyticsEvent(category: string, action: string, name?: string, value?: any, debounce?: boolean): void {
        const debounceAnalytics = (func: any): void => {
            if (this.analyticsDebounceTimer) {
                clearTimeout(this.analyticsDebounceTimer);
                this.analyticsDebounceTimer = 0;
            }
            this.analyticsDebounceTimer = setTimeout(() => {
                this.analyticsDebounceTimer = 0;
                func.apply(this);
            }, 1000) as any as number;
        };

        if (storefrontAnalytics && storefrontAnalytics.trackEvent) {
            if (debounce) {
                debounceAnalytics(() => {
                    this.addAnalyticsEvent(category, action, name, value, false);
                });
            } else {
                console.log('Add analytics event ' + category + ' - ' + action + ' ' + (name ? name + ': ' + value : ''));
                storefrontAnalytics.trackEvent(category, action, name || '', value || '');
            }
        }
    }

    private resizeImageContainerForTwoSidedProducts(): void {
        this.editorController.readyPromise().then(() => {
            this.productImageWidth = parseFloat(this.editorController.getCanvasElement().style.width) / 2;
            this.productImageContainer.style.width = this.productImageWidth + 'px';
            const previewContainerPaddingLeft = parseFloat(getComputedStyle(this.previewContainerEl).paddingLeft);
            this.productImageContainer.style.left = Math.round((this.previewContainerEl.offsetWidth -
                this.productImageContainer.offsetWidth) / 2) - previewContainerPaddingLeft + 'px';
        }, err => {
            console.error('Error resizing image container for two sided products', err);
        });
    }

    private getElementSize(element: HTMLElement): { width: number, height: number } {
        const style = window.getComputedStyle(element);
        const height = [
            'margin-top',
            'margin-bottom',
            'border-top',
            'border-bottom',
            'padding-top',
            'padding-bottom',
            'height'
        ]
            .map(k => parseInt(style.getPropertyValue(k), 10))
            .reduce((prev, cur) => prev + cur);
        const width = [
            'margin-left',
            'margin-right',
            'border-left',
            'border-right',
            'padding-left',
            'padding-right',
            'width'
        ]
            .map(k => parseInt(style.getPropertyValue(k), 10))
            .reduce((prev, cur) => prev + cur);
        return {width, height};
    }

    private enableResizeCanvasOnWindowSizeChange(): void {
        const debounce = (func: () => void, wait: number) => {
            let timeout: number;
            return () => {
                clearTimeout(timeout);
                timeout = setTimeout(() => func(), wait) as any as number;
            };
        };

        const handleResize = () => {
            this.productImageContainer.style.height = '100%';
            this.previewContainerEl.style.height = '100%';
            setTimeout(() => {
                const previewContainerWidth = this.getElementSize(this.previewContainerEl).width;
                const maxWidth = previewContainerWidth * (this.isTwoSidedProduct ? 2 : 1);
                const maxHeight = this.productImageContainer.offsetHeight;
                this.productImageContainer.style.height = '';
                this.previewContainerEl.style.height = '';
                const width = this.editorController.getVisiblePagesWidth();
                const height = this.editorController.getVisiblePagesHeight();
                const maxSize = width > height ? maxWidth : maxHeight;
                this.editorController.resizeCanvas(width, height, maxSize);
                this.editorController.getFabricCanvas().renderAll();
                if (this.isTwoSidedProduct) {
                    this.resizeImageContainerForTwoSidedProducts();
                }
                if (!this.editUpload.classList.contains('hidden')) {
                    this.moveInlineUpload();
                }
                this.updateCanvasContainerStyle();
            });
        };

        window.addEventListener('resize', debounce(handleResize, 300));
    }

    private updateCanvasContainerStyle(): void {
        const canvasContainer = document.querySelector<HTMLDivElement>('.canvas-container');
        if (canvasContainer) {
            if (this.isTwoSidedProduct) {
                this.canvasContainerLeft = 0;
            } else {
                this.canvasContainerLeft = Math.round((this.productImageContainer.offsetWidth - canvasContainer.offsetWidth) / 2);
                canvasContainer.style.left = this.canvasContainerLeft + 'px';
            }
        } else {
            this.addAnalyticsEvent('error', 'update-canvas-container-style', 'error', 'Cannot find canvas-container');
        }
    }

    private updateZoom(zoom: number): void {
        if (this.currentZone) {
            const templateZone = document.querySelector<HTMLDivElement>('.template-zone[data-free-object-id="' + this.currentFreeObjectId + '"]');

            const zoomState = this.getZoomState(zoom);
            if (zoomState === 'max') {
                const image = this.editorController.getCurrentZone().photoObject.image;
                this.zoom = Math.min(this.zoomMax, Math.min(MAX_IMAGE_SIZE / Math.max(image.width, image.height)));
                this.editorController.setCurrentZoneZoom(this.zoom, zoom);
            } else {
                this.zoom = zoom;
            }

            if (templateZone) {
                const zoomInButton = templateZone.querySelector<HTMLButtonElement>('.button-zoom-in');
                const zoomOutButton = templateZone.querySelector<HTMLButtonElement>('.button-zoom-out');
                if (zoomInButton) {
                    zoomInButton.disabled = zoomState === 'max' || zoomState === 'none';
                }
                if (zoomOutButton) {
                    zoomOutButton.disabled = zoomState === 'min' || zoomState === 'none';
                }
            }
        }
    }

    private getZoomState(zoom: number): '' | 'min' | 'max' | 'none' {
        let zoomState: '' | 'min' | 'max' | 'none' = '';
        const freeObject = this.editorController.getCurrentFreeObject();
        if (zoom === -1 || (!freeObject.imageEdits || !freeObject.imageEdits.fullImageFilename)) {  // If we don't have an image...
            zoomState = 'none';
        } else {
            const photoObject = this.editorController.getCurrentZone().photoObject;
            if (zoom >= this.zoomMax || (Math.max(photoObject.image.width, photoObject.image.height) * zoom) >= MAX_IMAGE_SIZE) {
                zoomState = 'max';
            } else if (zoom <= photoObject.getMinZoom()) {
                zoomState = 'min';
            }
        }
        return zoomState;
    }

    private addControllerEventListeners(): void {

        this.editorController.onZoneChange((freeObjectId: number | null, selectElement: IEditorElement | null, target: IEditorShape | null): void => {
            this.zoneChanged(freeObjectId, selectElement, target);
        });

        // When a zone is modified, we need to update the text edit input (if it is a text zone) and update the state of the Save/Preview/Retry buttons...
        this.editorController.onZoneModified((_: number | null, change): void => {
            this.zoneModified(change);
        });

        //@ts-ignore
        // When text is modified, we need to update the text edit input
        this.editorController.onTextChanged((freeObjectId: number, text: IEditorText): void => {
            this.updateTextEdit(freeObjectId, text.text);
            if (text.autoFontSize) {
                if (text.autoFontSizeAdjust()) {
                    this.updateTextSize(freeObjectId, text.fontSize, true, null);
                }
            }
        });

        this.editorController.onExternalLinkedZone((textZoneId: number, type: TextChangeType, value: string | number | boolean): void => {
            let zone: IFreeObject | undefined;
            for (let i = 0; i < this.pages.length; i++) {
                if (i !== this.currentPage) {
                    zone = this.pages[i].edits.freeObject.find(freeObject => freeObject.textZoneId == textZoneId);
                    if (zone) {
                        break;
                    }
                }
            }
            if (zone && zone.textEdits) {
                switch (type) {
                    case 'size':
                        zone.textEdits.rootStyle!.fontSize = parseInt(value.toString());
                        break;
                    case 'color':
                        zone.textEdits.rootStyle!.fill = value.toString();
                        break;
                    case 'align':
                        zone.textEdits.rootStyle!.textAlign = value.toString();
                        break;
                    case 'bold':
                        zone.textEdits.rootStyle!.fontStyle = value ? 'bold' : 'normal';
                        break;
                    case 'italic':
                        zone.textEdits.rootStyle!.fontStyle = value ? 'italic' : 'normal';
                        break;
                    case 'underline':
                        zone.textEdits.rootStyle!.underline = !!value;
                        break;
                    case 'text':
                        zone.textEdits.text = value.toString();
                        zone.textEdits.edited = true;
                        break;
                    default:
                        console.log('Invalid text options: ' + type);
                        // Shouldn't happen..
                        break;

                }
            }
        });

        this.editorController.onTextOptionsChanged((freeObjectId: number, type: 'text' | 'size' | 'color' | 'align' | 'font' | 'bold' | 'italic' | 'underline', value: string | number | boolean): void => {
            switch (type) {
                case 'size':
                    this.updateTextSize(freeObjectId, value as number, false, null);
                    break;
                case 'color':
                    this.updateTextColor(freeObjectId, value as string);
                    break;
                case 'align':
                    this.updateTextAlign(freeObjectId, value as string);
                    break;
                case 'bold':
                    this.updateTextBold(freeObjectId, value as boolean);
                    break;
                case 'italic':
                    this.updateTextItalic(freeObjectId, value as boolean);
                    break;
                case 'underline':
                    this.updateTextUnderline(freeObjectId, value as boolean);
                    break;
                case 'text':
                default:
                    console.log('Invalid text options: ' + type);
                    // Shouldn't happen..
                    break;

            }
        });

        // If an image is dropped on the zone, act like a file upload...
        this.editorController.onImageDropped((freeObjectId: number | null, fileName): void => {
            this.addAnalyticsEvent('addImage', 'image-dropped', 'filename', fileName);
            if (fileName != null) {
                this.editUpload.classList.add('hidden');
                this.updateFreeObjectImage(freeObjectId, fileName, true);
                this.updatePhotoZoneWithImageName(fileName, freeObjectId || 0);
            } else {
                this.showHideInvalidFileTypeError();
            }
        });


        // TODO: CLEAN THIS UP, currently this is the very hacky way to use the customerEditable flag
        //       to prevent the user from selecting non-customer editable zones.  It is done here to
        //       effect customizer or other projects that use the editor.
        const shouldClearSelection = (SF.Editor.Canvas as any).prototype._shouldClearSelection;
        (SF.Editor.Canvas as any).prototype._shouldClearSelection = function (e: Event, target: any): boolean {
            if (target && typeof target.customerEditable !== 'undefined' && !target.customerEditable) {
                target.selectable = false;
                return true;
            }
            return shouldClearSelection.call(this, e, target);
        };

    }

    private fixTextFromOptions(text: string, zoneTextEdits: ITextEdits): string {
        // Remove non-ascii characters, note: this will not allow non-latin characters and may have to be made more robust in the future.

        text = text.replace(/[^\x00-\xff]/g, '');
        if (zoneTextEdits.rootStyle.maxCharacters > 0 && text.length > zoneTextEdits.rootStyle.maxCharacters) {
            text = text.substring(0, zoneTextEdits.rootStyle.maxCharacters);
        }
        if (zoneTextEdits.rootStyle.textTransform === 'uppercase') {
            text = text.toUpperCase();
        } else if (zoneTextEdits.rootStyle.textTransform === 'lowercase') {
            text = text.toLowerCase();
        } else if (zoneTextEdits.rootStyle.textTransform === 'capitalize') {
            text = text.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
        } else if (zoneTextEdits.rootStyle.textTransform === 'numeric') {
            text = text.replace(/\D+/g, '');
        }

        return text;
    }

    private zoneModified(change: { type: "text" | "image"; text?: string; zoom?: number, zone: IEditorShape }): void {
        // Don't change the Reset if there is no photo and nothing has changed...
        if ((change.zone && change.zone.type === 'photo-group' && change.zone.photoObject.image === null) || change.type === 'text') {
            return;
        }
        if (change.type === 'image') {
            this.updateZoom(change.zoom);
        }
        this.isDirtyPage[this.currentPage] = true;
        this.changeEdits(false);
    }

    private zoneChanged(freeObjectId: number | null, selectElement: IEditorElement | null, target: IEditorShape | null): void {
        this.editUpload.classList.add('hidden');
        this.currentFreeObjectId = freeObjectId;
        this.currentZone = selectElement;
        const selectImageOrText = document.querySelector<HTMLDivElement>('#select-image-or-text');
        const editorCanvas = target && (target.canvas as unknown as IEditorCanvas);
        this.zoomMax = editorCanvas && editorCanvas.errorConfiguration ?
            editorCanvas.errorConfiguration.zoomError : 100;

        if (freeObjectId != null || (selectElement instanceof SF.Model.PhotoElement && selectElement.image)) {
            const activeZone = document.querySelector<HTMLDivElement>('.template-zone[data-free-object-id="' + freeObjectId + '"]');
            if (activeZone || selectElement instanceof SF.Model.TextElement) {
                activeZone?.scrollIntoView({behavior: 'smooth', block: 'start'});
                const isPhotoZone = selectElement instanceof SF.Model.PhotoElement;
                this.setCurrentActiveZone(activeZone, activeZone ? activeZone.dataset.freeObjectId : '1');
                if (isPhotoZone) {
                    if (selectElement && !selectElement.image && this.loaded) {
                        this.showInlineUpload();
                    } else if (selectElement && selectElement.image) {
                        this.updateZoom(target.getZoom());
                    } else {
                        this.updateZoom(-1);
                    }
                }
            } else if (!selectElement.image || !selectElement.image.name) {
                this.editUpload.classList.remove('hidden');
            }
            if (selectImageOrText && (selectElement instanceof SF.Model.PhotoElement || selectElement instanceof SF.Model.TextElement)) {
                selectImageOrText.style.display = 'none';
            }

        } else {
            const templateZones = document.querySelectorAll<HTMLDivElement>('.template-zone');
            // if there are more than one template zones or there is one template zone and the zone is unselected
            if (templateZones.length > 1 || (templateZones.length === 1 && freeObjectId === null && !selectElement && !target)) {
                if (templateZones.length >= 1) {
                    const visibleTemplateZone = document.querySelector<HTMLDivElement>('.template-zone.visible');
                    if (visibleTemplateZone) {
                        visibleTemplateZone.classList.remove('visible');
                    }
                }
                if (selectImageOrText) {
                    selectImageOrText.style.display = 'block';
                }
            } else if (templateZones.length === 1) {
                this.editorController.setCurrentZoneByFreeObjectId(parseInt(templateZones[0].dataset.freeObjectId, 10));
            }
        }

        if (freeObjectId === null && selectElement === null && target === null) {
            this.editPageContent.querySelectorAll<HTMLElement>('.text-zone.is-active').forEach(searchSelect => {
                searchSelect.classList.remove('is-active');
            });
        }

        this.changeEdits(true);
    }

    private updatePhotoZoneWithImageName(fileName: string, freeObjectId: number): void {
        document.getElementById('zone-file-name-' + freeObjectId)!.innerText = fileName;
        const templateZone = document.querySelector<HTMLDivElement>('.template-zone[data-free-object-id="' + this.currentZone.freeObjectId + '"]');
        if (templateZone) {
            templateZone.classList.add('has-photo');
        }
    }

    // Update the text edit input box
    private updateTextEdit(freeObjectId: number, text: string): void {
        const textEl = document.querySelector<HTMLInputElement>('input#zone-text-' + freeObjectId + '');
        if (textEl && textEl.value !== text) {
            const textValue = text.replace(/\r?\n|\r/g, ' ');
            textEl.value = textValue;
            const textTab = document.querySelector<HTMLSpanElement>('li[data-free-object-id="' + freeObjectId + '"] span.text-name');
            if (textTab) {
                textTab.innerText = textValue;
            }
            const currentZone = this.editorController.getCurrentZone();
            if (currentZone?.linkedZoneId) {
                this.editorController.updateLinkedText(currentZone as unknown as IEditorText, 'text', text);
            }
        }
    }

    // Update the fontsize select
    private updateTextSize(freeObjectId: number, textSize: number, autoUpdated: boolean, selectEl: HTMLSelectElement | null): void {
        selectEl = selectEl || document.querySelector<HTMLSelectElement>('select#zone-font-size-' + freeObjectId);
        if (selectEl) {
            // const previousOption = selectEl.options[0];
            let selectedIndexSet = false;
            for (let i = 0; i < selectEl.options.length; i++) {
                const optionValue = parseInt(selectEl.options[i].value, 10);
                if (!selectedIndexSet && optionValue === textSize) {
                    selectEl.selectedIndex = i;
                    selectedIndexSet = true;
                    if (!autoUpdated) {
                        return;
                    }
                } else if (!selectedIndexSet && optionValue > textSize) {
                    selectEl.selectedIndex = i - 1;
                    selectedIndexSet = true;
                    if (!autoUpdated) {
                        return;
                    }
                }
                selectEl.options[i].style.display = autoUpdated && optionValue > textSize ? 'none' : '';
            }
        }
    }

    // Update the text color
    private updateTextColor(freeObjectId: number, textColor: string): void {
        const colorEl = document.querySelector<HTMLInputElement>('input#zone-text-color-' + freeObjectId + '');
        if (colorEl && colorEl.value !== textColor) {
            colorEl.value = textColor;
        }
    }

    private updateTextAlign(freeObjectId: number, textAlign: string): void {
        const alignEl = document.querySelector<HTMLInputElement>('input#zone-text-align-' + textAlign + '-' + freeObjectId + '');
        if (alignEl) {
            alignEl.checked = true;
        }
    }

    private updateTextBold(freeObjectId: number, bold: boolean): void {
        const boldEl = document.querySelector<HTMLInputElement>('input#zone-font-bold-' + freeObjectId + '');
        if (boldEl) {
            boldEl.checked = bold;
        }
    }

    private updateTextItalic(freeObjectId: number, italic: boolean): void {
        const italicEl = document.querySelector<HTMLInputElement>('input#zone-font-italic-' + freeObjectId + '');
        if (italicEl) {
            italicEl.checked = italic;
        }
    }

    private updateTextUnderline(freeObjectId: number, underline: boolean): void {
        const underlineEl = document.querySelector<HTMLInputElement>('input#zone-font-underline-' + freeObjectId + '');
        if (underlineEl) {
            underlineEl.checked = underline;
        }
    }

    private showHideInvalidFileTypeError(): void {
        this.showOkPrompt(
            SF.tr('ERROR_INVALID_FILE_TITLE', {}, 'Error Loading Image', false),
            SF.tr('ERROR_INVALID_FILE_TYPE', {}, 'Invalid file, only valid images are supported', false),
            SF.tr('BUTTON_OK', {}, 'OK', false), () => {
                this.hidePrompt();
                this.reEnablePage();
                this.saveButton.classList.remove('is-loading');
                this.spinner.classList.remove('spin');
            }
        );
    }

    // Inline uploads are shown when the user hasn't uploaded an image in a zone yet.
    private showInlineUpload(): void {
        if (!this.currentZone) {
            setTimeout(() => {
                this.showInlineUpload();
            }, 100);
            return;
        }

        this.moveInlineUpload();

        // This has to happen before we get the width and height, to have things centered correctly
        this.editUpload.classList.remove('hidden');

        this.editUpload.dataset.freeObjectId = this.currentFreeObjectId.toString();
        const fileInput = this.editUpload.querySelector<HTMLInputElement>('.file-input');
        if (fileInput) {
            fileInput.dataset.freeObjectId = this.currentFreeObjectId.toString();
        } else {
            console.error('Unable to find file input in edit upload');
        }
    }

    private moveInlineUpload() {
        setTimeout(() => {
            const scale = this.editorController.getScale();
            const width = Math.round(this.currentZone.width * scale);
            const height = Math.round(this.currentZone.height * scale);
            ['small', 'xsmall', 'xxsmall', 'xxxsmall'].forEach(size => {
                this.editUpload.classList.remove(size);
            });

            if (width < 180) {
                this.editUpload.classList.add(this.getSizeFromWidth(width));
            }

            const fabricCanvas = this.editorController.getFabricCanvas();
            const uploadLeft = this.canvasContainerLeft + fabricCanvas.marginLeft + Math.round((this.currentZone.x * scale) - (this.editUpload.offsetWidth / 2));
            const uploadTop = fabricCanvas.marginTop + Math.round((this.currentZone.y * scale) - (this.editUpload.offsetHeight / 2));
            this.applyStyle(this.editUpload, {
                maxWidth: width + 'px',
                maxHeight: height + 'px',
                left: uploadLeft + 'px',
                top: uploadTop + 'px',
            });
        }, 50);
    }

    private startZooming(zoomIn: boolean): void {
        const photoObject = this.editorController.getCurrentZone().photoObject;
        const zoomMin = photoObject.getMinZoom();
        if (!this.isZooming || (zoomIn && (this.zoom >= this.zoomMax || this.zoom * photoObject.image.width > MAX_IMAGE_SIZE ||
            this.zoom * photoObject.image.height > MAX_IMAGE_SIZE)) || (!zoomIn && this.zoom <= zoomMin)) {
            this.updateZoom(this.zoom);
            this.resetButton.disabled = false;
            return;
        }
        const z = zoomIn ? ZOOM_DELTA : 1 / ZOOM_DELTA;
        let newZoom = this.zoom * z;
        if (newZoom > this.zoomMax) {
            newZoom = this.zoomMax;
        } else if (newZoom < zoomMin) {
            newZoom = zoomMin;
        }
        this.editorController.setCurrentZoneZoom(newZoom, this.zoom);
        this.zoom = newZoom;
        setTimeout(() => {
            this.startZooming(zoomIn);
        }, 50);
    }

    // Add the default UI event listeners...
    private addEventListeners(): void {

        SF.addEventListener(clickEvent, '#front-back-tabs li', (e: Event, targetEl: HTMLElement): void => {
            this.addAnalyticsEvent('buttonClicked', 'front-back-changed');
            this.frontBackTabClick(e, targetEl);
        });

        SF.addEventListener(clickEvent, '#page-tabs li', (_: Event, targetEl: HTMLElement): void => {
            this.addAnalyticsEvent('buttonClicked', 'tab-changed');
            // exit text editing if text is still in editing mode when user goes to a different page
            this.editorController.exitTextEditing(true, false);
            this.changePageClick(targetEl, true).then(() => {
                console.log('changePageClick done');
            }, err => {
                console.error('changePageClick error', err);
            });
        });

        SF.addEventListener(mouseDownEvent, '#preview-modal .button-close', (_, el) => {
            if (el.id === 'preview-close' && this.closePreviewResolve) {
                this.closePreviewResolve();
                this.closePreviewResolve = undefined;
            }
            this.editorController.setMaskOverlay();
            this.editorController.setNonPrintableBackground();
            this.previewModal.style.display = 'none';
        });

        SF.addEventListener(mouseStartEvent, '.button-zoom-in', (ev: Event, el: HTMLElement) => {
            if ((el as HTMLInputElement).disabled) {
                return;
            }
            const zoomState = this.getZoomState(this.zoom);
            if (zoomState === 'max' || zoomState === 'none') {
                return;
            }

            if (!window.TouchEvent || !(ev instanceof TouchEvent)) {
                ev.preventDefault();
            }
            this.isZooming = true;
            this.addAnalyticsEvent('zoom', 'zoom-in-button');
            this.startZooming(true);
        });

        SF.addEventListener(mouseStartEvent, '.button-zoom-out', (ev, el: HTMLElement) => {
            if ((el as HTMLButtonElement).disabled) {
                return;
            }
            const zoomState = this.getZoomState(this.zoom);
            if (zoomState === 'min' || zoomState === 'none') {
                return;
            }
            if (!window.TouchEvent || !(ev instanceof TouchEvent)) {
                ev.preventDefault();
            }
            this.isZooming = true;
            this.addAnalyticsEvent('zoom', 'zoom-out-button');
            this.startZooming(false);
        });

        SF.addEventListener(['mouseup', ...clickEvent], '.button-zoom-in, .button-zoom-out', ev => {
            ev.preventDefault();
            this.isZooming = false;
        });

        SF.addEventListener('focus', '.zone-text', (_, el: HTMLElement): void => {
            const freeObjectId = el.dataset.freeObjectId as string;
            if (el.dataset.default === (el as HTMLInputElement).value) {
                (el as HTMLInputElement).value = '';
                this.hasChanged[freeObjectId] = false;
            } else {
                this.hasChanged[freeObjectId] = true;
            }
            this.setCurrentActiveZone(null, freeObjectId);
        });

        SF.addEventListener('change', '.zone-text', (_, el: HTMLElement): void => {
            const freeObjectId = el.dataset.freeObjectId as string;
            this.hasChanged[freeObjectId] = true;
        });

        SF.addEventListener('blur', '.zone-text', (_, el: HTMLElement): void => {
            const freeObjectId = el.dataset.freeObjectId as string;
            if (!window.isEnteringText && this.hasChanged[freeObjectId] === false && (el as HTMLInputElement).value === '') {
                (el as HTMLInputElement).value = el.dataset.default;
                this.editorController.setTextZoneOptionsByFreeObjectId(parseInt(freeObjectId), 'text', el.dataset.default, !this.currentZone?.edited);
            }
        });

        SF.addEventListener(clickEvent, '.template-zone.photo-zone', (ev: Event, el: HTMLElement) => {
            const freeObjectId = el.dataset.freeObjectId;
            const templateZone = document.querySelector('.template-zone[data-free-object-id="' + freeObjectId + '"]');
            if (templateZone.classList.contains('has-photo') && !templateZone.classList.contains('is-active')) {
                ev.stopPropagation();
                ev.preventDefault();
            }
            this.setCurrentActiveZone(null, freeObjectId);
        });

        if (this.largePreviewButton) {
            this.largePreviewButton.addEventListener('click', () => {
                this.addAnalyticsEvent('buttonClicked', 'largeShowPreview');

                if (this.threeDModel) {
                    void this.showThreeDPreview();
                } else {
                    void this.showItePreview();
                }
                this.largePreviewButton.classList.remove('cta');
                this.saveButton.classList.add('cta');
            });
        }

        // Make sure we don't submit the form if the user hits enter in a text input...
        document.querySelectorAll('form').forEach(form => {
            form.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    return false;
                }
            });
        });

        this.resetButton.addEventListener('click', () => {
            this.addAnalyticsEvent('buttonClicked', 'reset-clicked');
            const currentPage = this.editorPage[this.currentPage];
            this.editorController.reset([currentPage]);
            this.isDirtyPage[this.currentPage] = false;

            // Clear all the file input texts.
            document.querySelectorAll<HTMLInputElement>('input.file-input').forEach(el => el.value = '');

            this.changeEdits(true);
            this.setupEditorPanels();
            if (this.largePreviewButton) {
                this.largePreviewButton.classList.add('cta');
                this.saveButton.classList.remove('cta');
            }
            document.querySelectorAll<HTMLSpanElement>('span.zone-file-name').forEach(el => el.innerText = '');

            document.querySelectorAll<HTMLInputElement>('input.zone-text').forEach(el => {
                const freeObjectId = el.dataset.freeObjectId;
                const textElement = currentPage.elements.find(element =>
                    element instanceof SF.Model.TextElement && (element as any).freeObjectId == freeObjectId);

                el.dataset.default = textElement ? this.getDefaultText(textElement.textEdits) : '';
                el.setAttribute('value', el.dataset.default);
            });
        });

        const termsIAgree = document.querySelector<HTMLInputElement>('#button-terms-agree');
        termsIAgree.addEventListener('click', () => {
            const newFreeObject = this.pages[0].edits.freeObject.find(freeObject => CustomizerImagingUtils.isPhotoZone(freeObject) && (!freeObject.imageEdits || !freeObject.imageEdits.fullImageFilename));
            if (newFreeObject) {
                // There is only 1 freeObject and it is an image...
                this.showInlineUpload();
            }
            this.addAnalyticsEvent('buttonClicked', 'terms-and-conditions-accepted');
            this.hideTermsShowControls();
        });

        if (this.termsAndConditionsFileUpload) {
            this.termsAndConditionsFileUpload.addEventListener('click', () => {
                termsIAgree.checked = true;
                termsIAgree.dispatchEvent(new Event('click'));
            });
        }

        this.saveButton.addEventListener('click', (): void => {
            // do not call clearSelection if we only need to exit text editing. clearSelection does extra stuff with side effects...
            this.addAnalyticsEvent('buttonClicked', 'save-clicked');
            try {
                this.editorController.exitTextEditing(false, true);
                this.disablePage();
                const selectImageOrText = document.querySelector<HTMLDivElement>('#select-image-or-text');
                if (selectImageOrText) {
                    selectImageOrText.style.display = 'none';
                }
                this.saveProductToCart().then(() => {
                    console.log('Product saved');
                }, err => {
                    console.error('Error saving project to cart', err);
                    this.addAnalyticsEvent('error', 'save-project-to-cart', 'error', err);
                });
            } catch (error) {
                console.log(error);
                this.showTextPreviewError();
            }
        });

        document.querySelectorAll<HTMLButtonElement>('#button-cancel, #terms-cancel-button').forEach(button => {
            button.addEventListener('click', (): void => {
                this.addAnalyticsEvent('exit', 'terms-and-conditions-cancel');
                parent.postMessage({
                    session_identifier: this.sessionId
                }, '*');
                if (this.orderComplete) {
                    this.orderComplete(false);
                }
                document.querySelector<HTMLDivElement>('#terms-and-conditions')!.style.display = 'none';
            });
        });

        window.history.pushState({personalize: 1}, null);
        window.addEventListener('popstate', (): void => {
            this.addAnalyticsEvent('exit', 'exit-by-back-button');
            console.log('Bye From Window');
            parent.postMessage({
                session_identifier: this.sessionId
            }, '*');
            if (this.orderComplete) {
                this.orderComplete(false);
            }
        });

        SF.addEventListener('input', '.zone-text', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            const textZone = this.editorController.setCurrentZoneTextOptions('text', targetEl.value);
            if (textZone) {
                if (textZone.text !== targetEl.value) {
                    targetEl.value = textZone.text;
                }
                const selectZone = document.querySelector<HTMLSelectElement>('select#zone-font-size-' + textZone.freeObjectId);
                if (selectZone && parseFloat(selectZone.value) > textZone.fontSize) {
                    this.updateTextSize(textZone.freeObjectId, textZone.fontSize, true, selectZone);
                }
                this.addAnalyticsEvent('textChanged', 'input-text', 'content', targetEl.value, true);
                this.isDirtyPage[this.currentPage] = true;
                this.changeEdits(false);
            }
        });

        SF.addEventListener('change', '.zone-font-size', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            const size = parseInt(targetEl.value);
            this.editorController.setCurrentZoneTextOptions('size', size);
            this.addAnalyticsEvent('textChanged', 'font-size', 'size', size, true);
            this.isDirtyPage[this.currentPage] = true;
            this.changeEdits(false);
        });

        SF.addEventListener('input', '.zone-text-color', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            this.editorController.setCurrentZoneTextOptions('color', targetEl.value);
            this.addAnalyticsEvent('textChanged', 'text-color', 'color', targetEl.value, true);
            this.isDirtyPage[this.currentPage] = true;
            this.changeEdits(false);
        });

        SF.addEventListener('change', '.zone-text-align', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            this.editorController.setCurrentZoneTextOptions('align', targetEl.value);
            this.addAnalyticsEvent('textChanged', 'text-align', 'align', targetEl.value, true);
            this.isDirtyPage[this.currentPage] = true;
            this.changeEdits(false);
        });

        SF.addEventListener('change', '.zone-font-bold', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            targetEl.checked = this.editorController.currentZoneToggleBold();
            this.addAnalyticsEvent('textChanged', 'bold');
            this.isDirtyPage[this.currentPage] = true;
            this.changeEdits(false);
        });

        SF.addEventListener('change', '.zone-font-italic', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            targetEl.checked = this.editorController.currentZoneToggleItalic();
            this.addAnalyticsEvent('textChanged', 'italic');
            this.changeEdits(false);
        });

        SF.addEventListener('change', '.zone-font-underline', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            targetEl.checked = this.editorController.currentZoneToggleUnderline();
            this.addAnalyticsEvent('textChanged', 'underline');
            this.isDirtyPage[this.currentPage] = true;
            this.changeEdits(false);
        });

        SF.addEventListener('change', '.file-input', (_: Event, el: HTMLElement) => {
            const targetEl = el as HTMLInputElement;
            this.addAnalyticsEvent('addImage', targetEl.id === '' ? 'template-image' : 'file-image', 'filename', targetEl.files[0].name);
            this.loadImageFromInputSelect(targetEl);
        });
    }

    private disablePage(): void {
        this.previewContainerEl.style.pointerEvents = 'none';
        document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('#edit-page-content input, #edit-page-content select').forEach(el => {
            el.disabled = true;
        });
        this.resetButton.disabled = true;
        if (this.largePreviewButton) {
            this.largePreviewButton.disabled = true;
        }

        document.querySelectorAll<HTMLButtonElement>('.button-zoom-in, .button-zoom-out').forEach(button => button.disabled = true);
        document.querySelector<HTMLButtonElement>('#button-cancel').disabled = true;
    }

    private loadImageFromInputSelect(targetEl: HTMLInputElement): void {
        const freeObjectId = parseInt(targetEl.dataset.freeObjectId);
        const imageFile = targetEl.files[0];
        this.loadImageForEditor(imageFile, freeObjectId, targetEl);
    }

    private loadImageForEditor(imageFile: File, freeObjectId: number, imageInput: HTMLInputElement): void {
        // Extract accepted image types from the input element's accept attribute,
        // construct a regular expression to match these file types, and check if the
        // uploaded file's name matches this regular expression.  In case the user
        // overrides the accept attribute, we will still only accept image files.
        const imageTypes = imageInput.accept.split(',').map(type => {
            if (type.length > 6) {
                const tmp = type.substring(6);
                if (tmp === 'jpeg') {
                    return 'jpg|jpeg';
                }
                return tmp;
            }
            return '';
        }).filter(type => type !== '').join('|');
        const regex = new RegExp('\.(' + imageTypes + ')$', 'i');
        if (imageFile.name.match(regex)) {
            this.pageDirtyBeforeImage = this.isDirtyPage[this.currentPage];
            this.disablePage();
            this.loadImageFromFile(freeObjectId, imageFile, imageInput).then(() => {
                imageInput.value = '';
            }).catch(err => {
                console.error('Error loading image', err);
                this.showHideInvalidFileTypeError();
            });
        } else {
            this.showHideInvalidFileTypeError();
        }
    }


    /**
     * Loads an image from a file and processes it to ensure it meets the size requirement.
     * @param freeObjectId - The ID of the free object associated with the image.
     * @param imageFile - The image file to be loaded and processed.
     * @param imageInput - The HTML input element that triggered the image upload.
     */
    private async loadImageFromFile(freeObjectId: number, imageFile: File, imageInput: HTMLInputElement): Promise<void> {
        return new Promise<void>(async (resolve:() => void, reject:(err:string) => void) => {
            this.spinner.classList.add('spin');
            let img;
            try {
                img = await this.processImage(imageFile);
            } catch (err) {
                reject(err);
                return;
            }

            const freeObject = this.updateFreeObjectImage(freeObjectId, imageFile.name, false);
            this.editorController.setCurrentZoneImage(Renderer.getEditorImageFromImage(img, imageFile.name));

            setTimeout(async () => {
                this.spinner.classList.remove('spin');
                this.reEnablePage();
                if (this.editorController.photoZoneHasError(this.editorController.getCurrentZone())) {
                    this.userImageErrorSize();
                    this.removePhoto();
                    if (!this.editUpload.classList.contains('hidden')) {
                        this.moveInlineUpload();
                    }
                    imageInput.value = '';
                    return;
                }
                if (this.editorController.photoZoneHasWarning(this.editorController.getCurrentZone())) {
                    if (await this.userImageWarningSize()) {
                        this.removePhoto();
                        this.isDirtyPage[this.currentPage] = this.pageDirtyBeforeImage;
                        this.resetButton.disabled = !this.isDirtyPage[this.currentPage];
                        imageInput.value = '';
                        return;
                    }
                }

                const zoneFileName = document.querySelector<HTMLElement>(`#zone-file-name-${freeObject.freeObjectId}`);
                if (zoneFileName) {
                    zoneFileName.innerText = imageFile.name;
                }

                const templateZone = document.querySelector<HTMLDivElement>(
                    `.template-zone[data-free-object-id="${this.currentZone.freeObjectId}"]`
                );
                if (templateZone) {
                    templateZone.classList.add('has-photo');
                }

                this.editUpload.classList.add('hidden');
                this.isDirtyPage[this.currentPage] = true;
                this.changeEdits(false);
                resolve();
            }, 50);


        });
    }

    /**
     * Processes the image, ensuring its size is within the allowed limit by adjusting quality and resolution.
     * @param imageFile - The file to load
     * @returns A Promise resolving to the processed image element.
     */
    private async processImage(imageFile: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            let currentSize = imageFile.size;
            let step = 0;
            let width:number;
            let height:number;
            let strippedExif = false;
            const hasTransparency = imageFile.name.endsWith('.png') || imageFile.name.endsWith('.webp') || imageFile.name.endsWith('.gif');
            // [export function load(data: string | File): Promise<Tags>] exists in the typings for exifreader but is not recognized by typescript for some reason, so we have to ignore the error here.
            // @ts-ignore
            ExifReader.load(imageFile).then((tags: Tags) => {
                img.addEventListener('load', async () => {
                    const hasOrientationExif = tags && tags.Orientation && (tags.Orientation.value === 3 || tags.Orientation.value === 6 || tags.Orientation.value === 8);
                    if (currentSize <= this.maxImageSize) {
                        if (!hasOrientationExif || strippedExif) {
                            resolve(img);
                        } else {
                            strippedExif = true;
                            const strippedExifBlog = await this.getBlobFromImage(img, img.width, img.height, hasTransparency);
                            img.src = URL.createObjectURL(strippedExifBlog);
                        }
                    } else {
                        strippedExif = true;
                        if (step === 0) {
                            width = img.width;
                            height = img.height;
                        }
                        if (step > 0) {
                            // Calculate adaptive scaling factor based on size ratio
                            const desiredRatio = this.maxImageSize / currentSize;
                            let scale = Math.sqrt(desiredRatio);
                            // Apply safety factor to account for compression variances
                            const SAFETY_FACTOR = 0.9;
                            scale *= SAFETY_FACTOR;
                            // Ensure scale does not exceed 1 (no upscaling)
                            scale = Math.min(scale, 1);
                            // Optional: Prevent excessive scaling in one step
                            // scale = Math.max(scale, 0.5);
                            width = Math.floor(width * scale);
                            height = Math.floor(height * scale);
                        }
                        step += 1;
                        try {
                            const resizedBlob = await this.getBlobFromImage(img, width, height, hasTransparency);
                            currentSize = resizedBlob.size;
                            img.src = URL.createObjectURL(resizedBlob);
                        } catch (err) {
                            reject(err);
                        }
                    }
                });
                img.addEventListener('error', () => {
                    reject('Error loading image');
                });
                img.src = URL.createObjectURL(imageFile);
            }).catch((error: string) => {
                console.error('Failed to load exif data', error);
            });
        });
    }

    /**
     * Converts an image into a Blob while optionally compressing and resizing it.
     * @param image - The image element to process.
     * @param width - width to resize the image.
     * @param height - height to resize the image.
     * @param hasTransparency - whether or not to save as a webp
     * @returns A promise resolving to a Blob of the processed image.
     */
    private getBlobFromImage(image: HTMLImageElement, width: number, height: number, hasTransparency:boolean): Promise<Blob> {
        return new Promise((resolve:(blob:Blob) => void, reject:(err:string) => void) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject('Error getting context');
                return;
            }

            canvas.width = width || image.width;
            canvas.height = height || image.height;
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject('Error creating blob');
                }
            }, hasTransparency ? 'image/webp' : 'image/jpeg');
        });
    }


    private updateFreeObjectImage(freeObjectId: number, imageFileName: string, skipRemoveEditorImage: boolean): IFreeObject {
        const freeObject = this.editorController.getCurrentFreeObject();

        if (freeObject.uniquePhotoId && !skipRemoveEditorImage) {
            Renderer.removeEditorImage(freeObject.uniquePhotoId);
        }

        const oldFreeObject = this.pages[this.currentPage].edits.freeObject.find(edit => edit.freeObjectId === freeObjectId);

        // Remove the old photoId - otherwise we will not upload a new image and end up with a busted version of the old image stretched all wrong...
        if (oldFreeObject && oldFreeObject.photoId) {
            oldFreeObject.photoId = null;
        }

        this.freeObjectToFilename[freeObject.freeObjectId] = imageFileName;
        return freeObject;
    }

    private removePhoto(): void {
        this.editorController.removeCurrentZonePhoto();
        const templateZone = document.querySelector<HTMLDivElement>('.template-zone[data-free-object-id="' + this.currentZone.freeObjectId + '"]');
        if (templateZone) {
            templateZone.querySelector<HTMLSpanElement>('.zone-file-name')!.innerHTML = '';
            templateZone.querySelector<HTMLInputElement>('input')!.value = '';
            templateZone.classList.remove('has-photo');
        }
        this.isDirtyPage[this.currentPage] = true;
        this.editUpload.classList.remove('hidden');
        this.changeEdits(false);
        this.updateZoom(this.zoom);
    }

    private changeEdits(reset: boolean): void {
        const elements: IEditorElement[] = this.editorController.getPageElements();
        let allZonesFilled = elements.every(el => {
            return !((el instanceof SF.Model.PhotoElement && !el.image) ||
                (el instanceof SF.Model.TextElement && (el.editable || el.customerEditable) && !el.edited));
        });
        this.saveButton.disabled = !allZonesFilled || elements.length === 0;
        this.largePreviewButton.disabled = false;
        this.resetButton.disabled = (reset && !this.isDirtyPage[this.currentPage]) || !this.loaded;
    }

    private async saveProductToCart(): Promise<void> {
        // Remove empty texts
        const emptyTexts = this.editorController.removeEmptyTextZones();
        if (emptyTexts) {
            this.addAnalyticsEvent('validateText', 'removed-empty-texts', 'count', emptyTexts);
        }

        if ((this.threeDModel || this.itePreview) && this.showPreviewOnSave()) {
            this.addAnalyticsEvent('saveProductToCart', 'show-preview');
            if (this.threeDModel) {
                await this.showThreeDPreview(true);
            } else {
                await this.showItePreview(true);
            }
        }

        // Ensure the text is all filled in or warn the user...
        if (await this.validateText()) {

            // Show the spinner on the save button...
            this.saveButton.classList.add('is-loading');
            this.spinner.classList.add('spin');

            // Clone the old pages so that we don't lose photoIds on a second save...
            const oldPages: IPage[] = cloneDeep(this.pages);

            // Get the current page, the editorController returns multiple pages since in the mobile editor
            try {
                this.pages[this.currentPage] = (await this.editorController.getPage(this.cartPreview, this.projectId, true,
                    this.previewOutputFormat))[0];
            } catch (err) {
                if (err.type === 'textPreview') {
                    this.showTextPreviewError();
                    return;
                }
                console.log(err);
            }
            const invalidPageToEdit = await this.validateMultiPages();
            if (invalidPageToEdit >= 0) {
                this.addAnalyticsEvent('validateImages', 'failed');
                const title = SF.tr('PLEASE_CHECK_TITLE', {}, 'Please Check Product', false);
                const message = SF.tr('PLEASE_CHECK_MESSAGE', {}, 'You have not personalized this product.  Please ensure the product is personalized as desired', false);
                this.showOkPrompt(title, message, SF.tr('BUTTON_OK', {}, 'OK', false), async () => {
                    this.hidePrompt();
                    await this.changePageClick(document.querySelector<HTMLLIElement>(`li[data-page="${invalidPageToEdit}"]`), true);
                    this.reEnablePage();
                    this.saveButton.classList.remove('is-loading');
                    this.spinner.classList.remove('spin');
                });
            } else {

                try {
                    const photoZones: IFreeObject[] = [];
                    for (let i = 0; i < this.pages.length; i++) {
                        const page = this.pages[i];
                        page.edits.pageName = oldPages[i].edits.pageName;
                        for (let j = 0; j < page.edits.freeObject.length; j++) {
                            const freeObject = page.edits.freeObject[j];
                            let oldFreeObject = oldPages[i].edits.freeObject.find(obj => obj.freeObjectId === freeObject.freeObjectId);
                            if (!oldFreeObject) {
                                oldFreeObject = page.edits.freeObject[j];
                            }
                            if (freeObject.uniquePhotoId && !freeObject.photoId) {
                                freeObject.customerEditable = oldFreeObject.customerEditable;
                                if (oldFreeObject.photoId) {
                                    // Copy from the old image, this way we don't have to upload the image multiple times if there is a problem adding to the cart.
                                    freeObject.photoId = oldFreeObject.photoId;
                                    freeObject.imageEdits.fullImageFilename = oldFreeObject.imageEdits.fullImageFilename;
                                    freeObject.imageEdits.urlPrefix = oldFreeObject.imageEdits.urlPrefix;
                                    freeObject.imageEdits.smallImageWidth = oldFreeObject.imageEdits.smallImageWidth;
                                    freeObject.imageEdits.smallImageHeight = oldFreeObject.imageEdits.smallImageHeight;
                                    freeObject.imageEdits.smallImageFilename = oldFreeObject.imageEdits.smallImageFilename;
                                    freeObject.imageEdits.mediumImageWidth = oldFreeObject.imageEdits.mediumImageWidth;
                                    freeObject.imageEdits.mediumImageHeight = oldFreeObject.imageEdits.mediumImageHeight;
                                    freeObject.imageEdits.mediumImageFilename = oldFreeObject.imageEdits.mediumImageFilename;
                                    freeObject.imageEdits.largeImageWidth = oldFreeObject.imageEdits.largeImageWidth;
                                    freeObject.imageEdits.largeImageHeight = oldFreeObject.imageEdits.largeImageHeight;
                                    freeObject.imageEdits.largeImageFilename = oldFreeObject.imageEdits.largeImageFilename;
                                    freeObject.imageEdits.xlargeImageWidth = oldFreeObject.imageEdits.xlargeImageWidth;
                                    freeObject.imageEdits.xlargeImageHeight = oldFreeObject.imageEdits.xlargeImageHeight;
                                    freeObject.imageEdits.xlargeImageFilename = oldFreeObject.imageEdits.xlargeImageFilename;
                                } else {
                                    photoZones.push(freeObject);
                                }
                            } else if (freeObject.textEdits) {
                                if (!freeObject.textEdits.preview_image && oldFreeObject.textEdits.preview_image) {
                                    freeObject.textEdits.preview_image = oldFreeObject.textEdits.preview_image;
                                }
                                if (typeof oldFreeObject.customerEditable === 'boolean' && !oldFreeObject.customerEditable && oldFreeObject.textEdits.preview_file) {
                                    freeObject.textEdits.preview_file = oldFreeObject.textEdits.preview_file;
                                    freeObject.customerEditable = false;
                                }

                                // In validateText, if we choose printDefault we set edited in this.pages which will get written over by the current page...
                                if (!freeObject.textEdits.edited && oldFreeObject.textEdits.edited) {
                                    freeObject.textEdits.edited = true;
                                }
                            }
                        }
                    }

                    // Upload each photo and return a promise...  Await all the promises...
                    await Promise.all(photoZones.map(freeObject => {
                        return this.uploadZonePhoto(freeObject);
                    }));
                    for (let i = 0; i < this.pages.length; i++) {
                        // if the page hasn't been visited yet...
                        if (i !== this.currentPage && !this.pages[i].preview_image) {
                            const previewImages = await this.editorController.getSnapShotsFromAllPages(
                                [this.pages[i]], this.editorController.getPreviewImageSize(),
                                false, true, 'png'
                            );
                            this.pages[i].preview_image = previewImages[0];
                        }
                        try {
                            await this.checkForTextPreviewImages(i);
                        }
                        catch (err) {
                            this.showTextPreviewError();
                            return;
                        }
                    }

                    // Clears pattern image from the free object before saving the project. Pattern image will be recreated when canvas is rendered.
                    this.removePatternImagesFromPages();
                    let res: IProjectResult;
                    // Save the project to the cart...
                    try {
                        res = await this.jsonApi.saveProjectToCart(this.projectId,
                            this.productId, this.pages, [], this.quantity, this.cartPreview, this.previewKey);

                    } catch (err) {
                        this.addAnalyticsEvent('saveProject', 'failure');
                        const title = SF.tr('ERROR_SAVING_PROJECT', {}, 'Error Saving Project', false);
                        const serverError = SF.tr('SERVER_ERROR', {}, 'Network Error', false);
                        const message = SF.tr('ERROR_SAVING_PROJECT_MESSAGE', {ERROR: serverError}, 'Error "{{ERROR}}" saving project.  Please try again in a few minutes', false);
                        this.showOkPrompt(title, message, SF.tr('BUTTON_OK', {}, 'OK', false), () => {
                            this.hidePrompt();
                            this.reEnablePage();
                            this.saveButton.classList.remove('is-loading');
                            this.spinner.classList.remove('spin');
                        });
                        return;
                    }

                    this.addAnalyticsEvent('saveProject', 'success');

                    // Send a message to parent that we are done, pass back the session_identifier, the cart_item_id, and cart_preview_url...
                    parent.postMessage({
                        session_identifier: this.sessionId,
                        cart_item_id: res.cart_item_id,
                        cart_preview_url: res.preview_url
                    }, '*');
                    if (this.orderComplete) {
                        this.orderComplete(true, res.preview_url, res.cart_item_id);
                    }
                } catch (error) {
                    const match = error.match(/UserViewableException:(\[[^\]]*])/);
                    let message = SF.tr('SAVE_ERROR_MESSAGE', {ERROR: error}, 'Server error saving personalization.  Please try again in a few minutes', false);
                    if (match) {
                        message = JSON.parse(match[1])[0];
                    } else {
                        this.addAnalyticsEvent('error', 'save-project', 'error', error);
                    }
                    console.error('Error: ' + error);
                    const title = SF.tr('SAVE_ERROR_TITLE', {}, 'Error Saving Personalization', false);

                    this.showOkPrompt(title, message, SF.tr('BUTTON_OK', {}, 'OK', false), () => {
                        this.hidePrompt();
                        this.reEnablePage();
                        this.saveButton.classList.remove('is-loading');
                        this.spinner.classList.remove('spin');
                    });

                }
            }
        } else {
            this.reEnablePage();
            this.addAnalyticsEvent('validateText', 'cancelled');
        }
    }

    private removePatternImagesFromPages() {
        for (let i = 0; i < this.pages.length; i++) {
            for (let j = 0; j < this.pages[i].edits.freeObject.length; j++) {
                this.editorController.removePatternImage(this.pages[i].edits.freeObject[j]);
            }
        }
    }

    private async checkForTextPreviewImages(index: number): Promise<void> {
        const pageHasText = this.pages[index].edits.freeObject.filter((object: IFreeObject): boolean => {
            if (object.textEdits) {
                object.textEdits.preview_image = null;
                return !(typeof object.customerEditable === 'boolean' && !object.customerEditable && object.textEdits.preview_file);
            }
            return false;
        });
        if (pageHasText.length > 0) {
            await this.editorController.captureDefaultTextPreview(this.pages[index]);
        }
    }

    // Create the editor panels...
    private setupEditorPanels(): void {
        const {hasEditable, textObjects, photoObjects} = this.getEditableObjects();

        let templateZone1: HTMLDivElement | null = null;
        this.editPageContent = document.querySelector<HTMLDivElement>('#edit-page-content');

        // If we have no editable elements then we are done...
        if (hasEditable) {

            let totalZones = 0;
            let zoneHtml = '';
            let hasPhoto = false;

            for (let i = 0; i < photoObjects.length; i++) {
                const photoAndIndex = photoObjects[i];
                zoneHtml += photoZoneTemplate({
                    zoneIndex: totalZones.toString(),
                    freeObjectId: photoAndIndex.freeObjectId.toString(),
                    fileName: this.freeObjectToFilename[photoAndIndex.freeObjectId] || 'image_' + (photoAndIndex.freeObjectId + 1) + '.jpg',
                    name: photoAndIndex.name,
                    hasPhoto: !!(photoAndIndex.freeObject!.imageEdits!.fullImageFilename),
                    isTouchOnly,
                    tr: SF.tr
                });
                hasPhoto = true;
                totalZones += 1;
            }
            if (hasPhoto && textObjects.length > 0) {
                zoneHtml += '<div id="photo-divider"></div>';
            }
            for (let i = 0; i < textObjects.length; i++) {
                const textAndIndex = textObjects[i];
                zoneHtml += this.createTextZone(textAndIndex, totalZones);
                totalZones += 1;
            }


            this.editPageContent.innerHTML = zoneHtml;

            this.addDragDropEvents();
            templateZone1 = this.editPageContent.querySelector<HTMLDivElement>('#template-zone-0');
        } else {
            this.editPageContent.innerHTML = '';
        }

        this.editorController.readyPromise().then(() => {
            if (templateZone1) {
                templateZone1.classList.add('is-active');
                const freeObjectId = parseInt(templateZone1.dataset.freeObjectId, 10);
                this.editorController.setCurrentZoneByFreeObjectId(freeObjectId);
                if (templateZone1.dataset.type === 'photo') {
                    setTimeout(() => {
                        const templateZoneFreeObject = [...textObjects, ...photoObjects].find(freeObject => freeObject.freeObjectId === freeObjectId);
                        if (!templateZoneFreeObject.freeObject.imageEdits || !templateZoneFreeObject.freeObject.imageEdits.fullImageFilename) {
                            if (this.editUpload.classList.contains('hidden')) {
                                this.showInlineUpload();
                            }
                        } else {
                            const zone = this.editorController.getCurrentZone();
                            this.updateZoom(zone.getZoom());
                        }
                    }, 150);
                }
            }

            this.previewContainerEl.style.height = 'auto';
            if (window.innerWidth < 800) {
                setTimeout(() => {
                    const previewColumn = document.querySelector<HTMLDivElement>('#preview-column');
                    if (previewColumn) {
                        previewColumn.style.maxHeight = this.getElementSize(this.previewContainerEl).height + 'px';
                    }
                }, 0);
            }

            this.updateCanvasContainerStyle();
            this.loaded = true;
            // if ios...
            for (let i = 0; i < this.pages.length; i++) {
                this.editorController.captureDefaultTextPreview(this.pages[i]).catch((error: string) => {
                    console.error(error);
                    this.showTextPreviewError();
                });
            }

            // This basically sets the states of the Save/Preview/Reset buttons...
            this.changeEdits(false);
        }, err => {
            this.addAnalyticsEvent('error', 'editor-controller-readyPromise', 'error', err);
        });
    }

    private getEditableObjects(): {
        hasEditable: boolean,
        textObjects: IFreeObjectAndData[],
        photoObjects: IFreeObjectAndData[]
    } {
        const textObjects: IFreeObjectAndData[] = [];
        const photoObjects: IFreeObjectAndData[] = [];
        let hasEditable = false;


        for (let i = 0; i < this.edits.freeObject.length; i++) {
            const freeObject = this.edits.freeObject[i];

            // Make sure the freeObject is editable...
            if (this.canEditObject(freeObject)) {
                const item: IFreeObjectAndData = {
                    freeObjectId: freeObject.freeObjectId,
                    freeObject: freeObject
                };

                // If it is a text edit
                if (freeObject.textEdits) {
                    item.name = SF.tr('TEXT_TAB_NAME', {INDEX: textObjects.length + 1}, 'Text {{INDEX}}', false);
                    item.type = 'text';
                    textObjects.push(item);
                } else if (CustomizerImagingUtils.isPhotoZone(freeObject)) {
                    item.name = SF.tr('PHOTO_TAB_NAME', {INDEX: photoObjects.length + 1}, 'Photo', false); // Can be 'Photo {{INDEX}}' if desired
                    item.type = 'photo';
                    photoObjects.push(item);
                }
                hasEditable = true;
            }
        }
        return {hasEditable, textObjects, photoObjects};
    }

    private canEditObject(obj: IFreeObject): boolean {

        // If customerEditable is set, use its value
        // in printsuite (where customerEditable is set), fixed/locked property should not affect the editable state. fixed/locked is only
        // used in Printsuite front-end for designers to prevent unintended object movements.
        if (Object.hasOwn(obj, 'customerEditable')) {
            return obj.customerEditable;
        }

        // If fixed is true, always return false
        if (!obj.textEdits && Object.hasOwn(obj, 'fixed') && obj.fixed === true) {
            return false;
        }

        // If customerEditable is not set, check canEdit
        if (Object.hasOwn(obj, 'canEdit')) {
            return obj.canEdit;
        }

        // If neither customerEditable nor canEdit are set, default to false
        return false;
    }

    private addDragDropEvents(): void {
        document.querySelectorAll<HTMLDivElement>('.template-zone.photo-zone').forEach((zone: HTMLDivElement) => {
            zone.addEventListener('drop', (ev: DragEvent) => {
                this.setCurrentActiveZone(null, zone.dataset.freeObjectId);
                const freeObjectId = parseInt(zone.dataset.freeObjectId);
                const inputEl = zone.querySelector<HTMLInputElement>('input[type="file"]');

                if (ev.dataTransfer.items) {
                    for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                        const item = ev.dataTransfer.items[i];
                        // If dropped items aren't files, reject them
                        if (item.kind === 'file' && (item.type === 'image/png' || item.type === 'image/jpeg')) {
                            this.loadImageForEditor(item.getAsFile(), freeObjectId, inputEl);
                            ev.stopPropagation();
                            ev.preventDefault();
                            break;
                        }
                    }
                } else {
                    if (ev.dataTransfer.files.length > 0) {
                        this.loadImageForEditor(ev.dataTransfer.files[0], freeObjectId, inputEl);
                        ev.stopPropagation();
                        ev.preventDefault();
                    }
                }
            });

            zone.addEventListener('dragover', (e: Event) => {
                const ev = e as InputEvent;
                if (ev.dataTransfer.items) {
                    for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                        const item = ev.dataTransfer.items[i];
                        // If dropped items aren't files, reject them
                        if (item.kind === 'file' && (item.type === 'image/png' || item.type === 'image/jpeg')) {
                            ev.stopPropagation();
                            ev.preventDefault();
                            return true;
                        }
                    }
                } else {
                    if (ev.dataTransfer.files.length > 0) {
                        ev.stopPropagation();
                        ev.preventDefault();
                        return true;
                    }
                }

            });
        });
    }

    private createTextZone(freeObjectAndData: IFreeObjectAndData, totalZones: number): string {
        const freeObject = freeObjectAndData.freeObject;
        const textDefault = this.getText(freeObject.textEdits);

        freeObjectAndData.name = textDefault.length < 10 ? textDefault : textDefault.substring(0, 9) + '...';
        freeObject.textEdits.styles = {};

        let textColor = '#000000';
        if (freeObject.textEdits.rootStyle && freeObject.textEdits.rootStyle.fill) {
            textColor = ColorUtils.getHexColor(freeObject.textEdits.rootStyle.fill);
        }
        const textSize = freeObject.textEdits.rootStyle!.fontSize || 50;
        const textAlign = freeObject.textEdits.rootStyle!.textAlign || 'center';
        const fontBold = freeObject.textEdits.rootStyle!.fontWeight === 'bold';
        const fontItalic = freeObject.textEdits.rootStyle!.fontStyle === 'italic';
        const fontUnderline = freeObject.textEdits.rootStyle!.underline || false;
        let font: IEditorFont | null = null;
        if (freeObjectAndData.freeObject.textEdits.rootStyle) {
            font = this.fontService.get(freeObjectAndData.freeObject.textEdits.rootStyle.fontFamily);
        }

        const canBold = font && font.canBold(false);
        const canItalic = font && font.canItalicize(false);
        const canBoldItalic = font && font.canBold(true);

        return textZoneTemplate({
            zoneIndex: totalZones.toString(),
            freeObjectId: freeObjectAndData.freeObjectId.toString(),
            showTextControls: this.previewOutputFormat !== 'txt' && this.shouldShowTextControls(freeObject),
            showFontColor: this.shouldShowFontColor(freeObject),
            showFontSize: this.shouldShowFontSize(freeObject),
            showTextAlign: this.shouldShowTextAlign(freeObject),
            showTextStyle: this.shouldShowTextStyle(freeObject),
            textSize,
            textColor,
            textDefault,
            textAlign,
            fontBold,
            fontItalic,
            fontUnderline,
            canBold,
            canItalic,
            canBoldItalic: canBoldItalic ? '1' : '0',
            tr: SF.tr
        });
    }

    private shouldShowTextControls(freeObject: IFreeObject): boolean {
        if (Object.hasOwn(freeObject, 'customerEditable')) {
            return freeObject.customerEditable;
        }
        return !freeObject.fixed;
    }

    private shouldShowFontColor(freeObject: IFreeObject): boolean {
        if (Object.hasOwn(freeObject, 'customerEditable')) {
            return freeObject.customerEditable;
        }
        return !(freeObject.textEdits.rootStyle!.lockFontColor);
    }

    private shouldShowFontSize(freeObject: IFreeObject): boolean {
        if (Object.hasOwn(freeObject, 'customerEditable')) {
            return freeObject.customerEditable;
        }
        return !(freeObject.textEdits.rootStyle!.lockFontSize);
    }

    private shouldShowTextAlign(freeObject: IFreeObject): boolean {
        if (Object.hasOwn(freeObject, 'customerEditable')) {
            return freeObject.customerEditable;
        }
        return !(freeObject.textEdits.rootStyle!.lockTextAlign);
    }

    private shouldShowTextStyle(freeObject: IFreeObject): boolean {
        if (Object.hasOwn(freeObject, 'customerEditable')) {
            return freeObject.customerEditable;
        }
        return !(freeObject.textEdits.rootStyle!.lockTextStyle);
    }

    private setCurrentActiveZone(zone: HTMLElement | null, freeObjectId: string): void {
        document.querySelectorAll<HTMLElement>('.template-zone').forEach((el: HTMLElement): void => {
            el.classList.remove('is-active');
        });
        let activeZone: HTMLElement;
        if (zone) {
            activeZone = zone;

            if (activeZone && activeZone.dataset.type === 'text') {
                const textInput = activeZone.querySelector<HTMLInputElement>('.zone-text');
                setTimeout(() => {
                    textInput.select();
                    textInput.focus();
                }, 50);
            }
        } else if (freeObjectId) {
            activeZone = document.querySelector<HTMLDivElement>('.template-zone[data-free-object-id="' + freeObjectId + '"]');
        }
        if (activeZone) {
            activeZone.classList.add('is-active');
        }
        this.editorController.setCurrentZoneByFreeObjectId(parseInt(freeObjectId, 10));
    }

    private frontBackTabClick(_: Event, targetEl: HTMLElement): void {
        this.goToVirtualPagePage(targetEl);
        const pageElements: IEditorElement[] = this.editorController.getPageElements();
        const elementOnCurrentPage: IEditorElement = pageElements.find((pageElement: IEditorElement) => {
            const xPosition = pageElement.x - (pageElement.width / 2);
            const page = xPosition > (this.edits.width / 2) ? 2 : 1;
            return pageElement instanceof SF.Model.TextElement && page === this.currentVisiblePage;
        });

        if (elementOnCurrentPage) {
            const zone = document.querySelector<HTMLLIElement>('.template-zone[data-free-object-id="' + elementOnCurrentPage.freeObjectId + '"]');
            this.setCurrentActiveZone(zone, zone ? zone.dataset.freeObjectId : '1');
            this.editorController.setCurrentZoneByFreeObjectId(parseInt(zone.dataset.freeObjectId, 10));
        }
    }

    private async changePageClick(targetEl: HTMLElement, getTextScreenShot: boolean): Promise<void> {
        const previouslyActiveElements: HTMLElement[] = [];
        document.querySelectorAll<HTMLElement>('#page-tabs li').forEach((el: HTMLElement): void => {
            if (el.classList.contains('is-active')) {
                previouslyActiveElements.push(el);
            }
            el.classList.remove('is-active');
        });
        targetEl.classList.add('is-active');
        try {
            const pages = await this.editorController.getPage(this.cartPreview, this.projectId, getTextScreenShot, 'webp');
            const page = pages[0];
            this.fixFreeObjectIds(page);

            page.edits.freeObject.forEach(freeObject => {
                if (CustomizerImagingUtils.isPhotoZone(freeObject)
                    && freeObject.imageEdits.fullImageFilename
                    && freeObject.imageEdits.fullImageFilename.substring(0, 10) !== 'data:image'
                    && !freeObject.imageEdits.fullImageFilename.startsWith('blob:')) {
                    const image: IEditorImage = Renderer.getEditorImage(freeObject.uniquePhotoId);
                    if (image) {
                        freeObject.imageEdits.fullImageFilename = image.getThumb(freeObject.imageEdits.fullImageFilename).image;
                        freeObject.imageEdits.largeImageFilename = image.getThumb(freeObject.imageEdits.largeImageFilename).image;
                        freeObject.imageEdits.mediumImageFilename = image.getThumb(freeObject.imageEdits.mediumImageFilename).image;
                        freeObject.imageEdits.xlargeImageFilename = image.getThumb(freeObject.imageEdits.xlargeImageFilename).image;
                        freeObject.imageEdits.smallImageFilename = image.getThumb(freeObject.imageEdits.smallImageFilename).image;
                    } else if (freeObject.uniquePhotoId) {
                        console.error('Unable to get image from Image');
                        this.addAnalyticsEvent('error', 'unable to get image');
                    }
                }
            });
            // Preserve pageName, which gets lost in the editorController.getPage call
            page.edits.pageName = this.pages[this.currentPage].edits.pageName;
            this.pages[this.currentPage] = page;

            this.currentPage = parseInt(targetEl.dataset.page, 10);

            this.editorController.loadNewPage([this.pages[this.currentPage]]);
            this.edits = this.pages[this.currentPage].edits;
            this.setupEditorPanels();

            if (!this.editUpload.classList.contains('hidden')) {
                this.moveInlineUpload();
            }
        } catch (err) {
            if (err.type === 'textPreview') {
                targetEl.classList.remove('is-active');
                previouslyActiveElements.forEach(el => el.classList.add('is-active'));
                this.showTextPreviewError();
                throw err;
            }
        }
    }

    private goToVirtualPagePage(activeTab: HTMLElement): void {
        document.querySelectorAll<HTMLDivElement>('#front-back-tabs li').forEach((el: HTMLElement): void => {
            el.classList.remove('is-active');
        });
        activeTab.classList.add('is-active');
        this.currentVisiblePage = parseInt(activeTab.dataset.page, 10);

        let transformX;
        if (this.currentVisiblePage === 2) {
            transformX = this.productImageWidth * -1;
        } else {
            transformX = 0;
        }
        this.editorController.getContainer().style.transform = 'translateX(' + transformX + 'px)';
    }

    private showItePreview(waitForClose?: boolean): Promise<void> {
        this.previewSpinner.style.display = 'block';
        return new Promise(async (resolve: () => void, reject: (error: string) => void): Promise<void> => {
            let previewImage = document.querySelector<HTMLImageElement>('#previewer-container img.preview-image');
            if (!previewImage) {
                previewImage = document.createElement('img');
                previewImage.classList.add('preview-image');
                previewImage.style.display = 'none';
                previewImage.style.margin = '70px auto 0';
                const parentElement = document.getElementById('previewer-container');
                const buttonElement = document.getElementById('preview-close');
                parentElement.insertBefore(previewImage, buttonElement);
            } else {
                previewImage.style.display = 'none';
            }
            this.previewModal.style.display = 'flex';
            let images: (string | HTMLCanvasElement)[] = [];
            try {
                images = await this.getImagesForPreview();
            } catch (e) {
                reject(JSON.stringify(e));
            }

            let itePreview:string;

            if (this.currentPage > 0 && this.itePagePreviews.length > this.currentPage) {
                itePreview = this.itePagePreviews[this.currentPage];
            } else {
                itePreview = this.itePreview;
            }

            for (let i = 0; i < images.length; i++) {
                let image = images[i];
                let imageDataUrl: string;
                if (typeof image === 'string') {
                    if (image.substring(0, 5) !== 'data:') {
                        imageDataUrl = 'data:image/png;base64,' + image;
                    } else {
                        imageDataUrl = image;
                    }
                } else {
                    if (this.previewOutputFormat === 'jpg') {
                        image = await CustomizerImagingUtils.addWhiteBackground(image);
                    }
                    imageDataUrl = (image as HTMLCanvasElement).toDataURL(this.previewOutputFormat, 75);
                }
                // Attempting to fix PRST-1564
                if (!itePreview.includes('%IMAGE_FILE_')
                    && !itePreview.includes('%PAGE_')
                    && this.currentPage > 0
                    && this.itePagePreviews.length > this.currentPage) {
                    // If there are no %IMAGE_FILE% or %PAGE_% placeholder, and we are on a page > 0, then only replace the current page placeholder
                    if (i === this.currentPage) {
                        itePreview = itePreview.replaceAll('%IMAGE_FILE%', imageDataUrl);
                    }
                } else {
                    // looks like in some of our ITE strings, indexing does not start from _1, and we had image_file, image_file_2, image_file_3, ...!! I'm adding
                    // logic to handle both cases and hoping that it's not breaking products with older ITE strings.
                    if (i === 0 && itePreview.includes('%IMAGE_FILE_1%') && !itePreview.includes('%PAGE_1%')) {
                        itePreview = itePreview.replace('%IMAGE_FILE_1%', imageDataUrl);
                    }
                    itePreview = itePreview.replace('%IMAGE_FILE' + (i > 0 ? '_' + (i + 1) : '') + '%', imageDataUrl).replace('%PAGE_' + (i + 1) + '%', imageDataUrl);
                }
            }
            const iteUrl =  this.iteUrl.replace(/\/ite[^\/]*$/, '/');
            const localPreview = itePreview.replace(/(mk|am|cp)'[^']*\/([^\/]+\/content\/[^']+)'/g, (match, p1, p2) => {
                return `${p1}'${iteUrl}${p2}'`;
            });

            let previewImageSrc:null|string = null;
            const ite = new Ite();
            try {
                const success = await ite.parseMicrocode(localPreview);
                if (success) {
                    const canvas = ite.getCurrentImage();
                    if (canvas) {
                        // TODO: Consider using to blob to save on memory...
                        previewImageSrc = canvas.toDataURL('image/png');
                    } else {
                        console.error('Unable to get canvas after parsing microcode: ' + ite.getError());
                    }
                } else {
                    console.error('Error parsing ite:' + ite.getError());
                }

            } catch(err) {
                console.error('Error parsing ite:' + err);
            }

            if (!previewImageSrc) {
                try {
                    previewImageSrc = await this.jsonApi.getImageFromPostIte(this.iteUrl, itePreview);
                } catch (error) {
                    console.error('Error getting ite from server', error);
                }
            }

            this.previewSpinner.style.display = 'none';
            previewImage.src = previewImageSrc;
            previewImage.style.display = 'flex';

            if (waitForClose) {
                this.closePreviewResolve = resolve;
            } else {
                resolve();
            }
        });
    }

    private showThreeDPreview(waitForClose?: boolean): Promise<void> {
        return new Promise(async (resolve: () => void, reject: (error: string) => void): Promise<void> => {
            this.previewModal.style.display = 'flex';
            this.previewSpinner.style.display = 'block';
            if (this.previewer) {
                this.previewer.destroy();
            }
            setTimeout(async () => {
                this.previewer = new ProductPreviewer(document.getElementById('previewer-container'), this.threeDColor);
                this.previewer.loadModel(this.threeDModel);
                try {
                    const images = await this.getImagesForPreview();
                    this.previewSpinner.style.display = 'none';
                    if (images.length === 1 && typeof images[0] !== 'string') {
                        this.previewer.loadPhotoFromCanvas(images[0], PreviewType.Photo);
                    } else {
                        this.previewer.loadPhotoFromImageSources(images, PreviewType.Photo);
                    }
                } catch (e) {
                    this.addAnalyticsEvent('error', 'error-getting-preview-page', 'error', e);
                    reject(e);
                }
                if (waitForClose) {
                    this.closePreviewResolve = resolve;
                } else {
                    resolve();
                }
            }, 0);
        });

    }

    private async getImagesForPreview(): Promise<(HTMLCanvasElement | string)[]> {
        let canvasElement = await this.editorController.toCanvasElement(this.previewOutputFormat);
        this.editorController.resetCanvasThreeDPreview();
        return new Promise<(HTMLCanvasElement | string)[]>(async (resolve: (images: (HTMLCanvasElement | string)[]) => void, reject: (error: string) => void) => {
            if (this.pages.length === 1) {
                if (this.previewOutputFormat === 'jpg') {
                    canvasElement = await CustomizerImagingUtils.addWhiteBackground(canvasElement);
                }
                if (this.threeDOverlay.url) {
                    canvasElement = await this.blendOverlay(canvasElement, this.threeDOverlay);
                }
                resolve([canvasElement]);
            } else {
                const images: (HTMLCanvasElement | string)[] = [];
                for (let i = 0; i < this.pages.length; i++) {
                    if (i === this.currentPage) {
                        images[i] = canvasElement;
                    } else {
                        try {
                            images[i] = await this.getPreviewFromPage(this.pages[i]);
                        } catch (e) {
                            reject('Error getting preview page: ' + JSON.stringify(e));
                        }
                    }
                    if (this.previewOutputFormat === 'jpg') {
                        images[i] = await CustomizerImagingUtils.addWhiteBackground(images[i]);
                    }
                    if (this.threeDOverlay.url) {
                        if (images[i] instanceof HTMLCanvasElement) {
                            images[i] = await this.blendOverlay(images[i] as HTMLCanvasElement, this.threeDOverlay);
                        } else {
                            reject('Attempt to blendOverlay on a string');
                        }
                    }
                }
                resolve(images);
            }

        });
    }

    private uploadZonePhoto(freeObject: IFreeObject): Promise<void> {

        return new Promise(async (resolve: () => void, reject: (error: string) => void): Promise<void> => {
            const image: IEditorImage = Renderer.getEditorImage(freeObject.uniquePhotoId);
            if (!image) {
                resolve();
                return;
            }
            const file = await this.getSizedFileFromFreeObject(freeObject, image);

            this.jsonApi.uploadImage(
                file,
                this.projectId
            ).then(photoData => {
                const position = photoData.url.lastIndexOf('/') + 1;
                freeObject.photoId = photoData.photo_id;
                freeObject.imageEdits.fullImageFilename = photoData.url.substring(position);
                freeObject.imageEdits.urlPrefix = photoData.url.substring(0, position);
                const smallThumb = this.getThumb(photoData, 'CI_SIZE_MEDIUM');
                const mediumThumb = this.getThumb(photoData, 'CI_SIZE_LARGE');
                const largeThumb = this.getThumb(photoData, 'CI_SIZE_XLARGE');
                const xlargeThumb = this.getThumb(photoData, 'CI_SIZE_XXLARGE');
                if (!smallThumb || !mediumThumb || !largeThumb || !xlargeThumb) {
                    throw new Error('Unable to get thumb');
                }
                freeObject.imageEdits.smallImageWidth = smallThumb.width;
                freeObject.imageEdits.smallImageHeight = smallThumb.height;
                freeObject.imageEdits.smallImageFilename = smallThumb.filename;
                freeObject.imageEdits.mediumImageWidth = mediumThumb.width;
                freeObject.imageEdits.mediumImageHeight = mediumThumb.height;
                freeObject.imageEdits.mediumImageFilename = mediumThumb.filename;
                freeObject.imageEdits.largeImageWidth = largeThumb.width;
                freeObject.imageEdits.largeImageHeight = largeThumb.height;
                freeObject.imageEdits.largeImageFilename = largeThumb.filename;
                freeObject.imageEdits.xlargeImageWidth = xlargeThumb.width;
                freeObject.imageEdits.xlargeImageHeight = xlargeThumb.height;
                freeObject.imageEdits.xlargeImageFilename = xlargeThumb.filename;

                resolve();
            })
                .catch(error => {
                    if (error.response && error.response.data && error.response.data.error_response &&
                        error.response.data.error_response.message &&
                        error.response.data.error_response.message.indexOf('UserViewableException') > -1) {
                        error.message = error.response.data.error_response.message;
                    }
                    this.addAnalyticsEvent('error', 'upload-image', 'error', error.message);
                    reject('Unable to upload image: ' + error.message);
                });
        });
    }

    private getThumb(photoData: { thumb_list: IThumbList[] }, size: string): {
        filename: string,
        height: number,
        width: number
    } | null {
        const thumb: IThumbList = photoData.thumb_list.find(searchThumb => searchThumb.thumb_type === size);
        if (thumb) {
            const lastIndex = thumb.url.lastIndexOf('/');
            if (lastIndex > -1) {
                return {
                    filename: thumb.url.substring(lastIndex + 1),
                    width: thumb.width,
                    height: thumb.height
                };
            }
        }
        return null;
    }

    private getMimeTypeFromName(name:string):string {
        switch (name.toLowerCase()) {
            case 'png':
                return 'image/png';
            case 'gif':
                return 'image/gif';
            case 'webp':
                return 'image/webp';
            case 'jpg':
            case 'jpeg':
            default:
                return 'image/jpeg';
        }
    }

   private getSizedFileFromFreeObject(freeObject: IFreeObject, image: IEditorImage): Promise<File> {
       const img = image.getUrl('full');
       return new Promise((resolve, reject) => {

           // If the image is smaller than
           if (freeObject.imageEdits.zoom >= 100) {
               this.getFileFromOriginalImage(img, image).then(file => {
                   resolve(file);
               });

           } else {

               const newImage = new Image();
               newImage.onload = () => {
                   const canvas = document.createElement('canvas');
                   const ctx = canvas.getContext('2d');

                   if (!ctx) {
                       reject('Error getting context');
                       return;
                   }

                   const zoom = freeObject.imageEdits.zoom / 100;
                   const newWidth = Math.ceil(freeObject.imageEdits.fullImageWidth * zoom);
                   const newHeight = Math.ceil(freeObject.imageEdits.fullImageHeight * zoom);
                   freeObject.imageEdits.fullImageWidth = newWidth;
                   freeObject.imageEdits.fullImageHeight = newHeight;

                   if (zoom !== 1) {
                       freeObject.imageEdits.zoom = 100;
                   }

                   canvas.width = newWidth;
                   canvas.height = newHeight;
                   ctx.drawImage(newImage, 0, 0, newWidth, newHeight);

                   const imageData = ctx.getImageData(0, 0, newWidth, newHeight);
                   const hasTransparency = imageData.data.some((p, i) => i % 4 === 3 && p < 255);

                   const mimeType = hasTransparency ? 'image/webp' : 'image/jpeg';
                   const ext = hasTransparency ? 'webp' : 'jpg';
                   const currentExt = image.name.split('.').pop().toLowerCase();
                   if (currentExt !== ext) {
                       image.name = image.name.slice(0, image.name.lastIndexOf('.')) + '.' + ext;
                   }
                   const quality = 0.75; // Adjust quality as needed

                   canvas.toBlob((blob) => {
                       if (blob) {
                           const file = new File([blob], image.name, {type: mimeType});
                           resolve(file);
                       } else {
                           // don't use the resized image, just return the original. If canvas size exceeds the maximum size allowed by the browser, it will fail to create a blob.
                           this.getFileFromOriginalImage(img, image).then(file => {
                               resolve(file);
                           });
                       }
                   }, mimeType, quality);

               };
               newImage.onerror = () => reject('Error loading image');
               newImage.src = img;
           }
       });
   }

   private getFileFromOriginalImage(imgUrl: string, image: IEditorImage): Promise<File> {
       return new Promise((resolve) => {
           fetch(imgUrl).then(response => {
               response.blob().then(blob => {
                   resolve(new File([blob], image.name, {type: this.getMimeTypeFromName(image.name)}));
               });
           });
       });
   }
    private hidePrompt(): void {
        this.promptModal.style.display = 'none';
        for (let i = 0; i < 3; i++) {
            if (this.buttonCallback[i]) {
                const buttonEl = document.querySelector<HTMLButtonElement>('#prompt-button-' + (i + 1));
                if (buttonEl) {
                    buttonEl.removeEventListener('click', this.buttonCallback[i]);
                }
            }
        }
        document.querySelector('#prompt-modal button.close-button').removeEventListener('click', this.cancelButtonCallback);
    }

    private showPrompt(title: string, message: string,
                       buttons: {
                           text: string,
                           class?: string,
                           callback: () => Promise<void> | void | null
                       }[], cancelCallBack: () => void = null): void {
        const modalTitle = document.getElementById('modal-title');
        const modalMessage = document.getElementById('modal-message');
        if (modalTitle && modalMessage) {
            modalTitle.innerText = title;
            modalMessage.innerHTML = message;
        } else {
            console.error('Unable to show prompt, attempt to do the next best thing with an alert');
            this.addAnalyticsEvent('error', 'unable-to-show-prompt', 'message', message);
            alert(message);
            return;
        }
        for (let i = 0; i < 3; i++) {
            const buttonEl = document.querySelector<HTMLButtonElement>('#prompt-button-' + (i + 1));
            if (buttonEl) {
                const button = buttons[i];
                if (button && button.text) {
                    buttonEl.style.display = 'inline-flex';
                    buttonEl.innerText = button.text;
                    buttonEl.className = 'button modal-button';        // Reset to the classlist to button
                    if (button.class) {
                        buttonEl.classList.add(button.class);
                    }
                    if (!button.callback) {
                        button.callback = (): void => {
                            this.hidePrompt();
                        };
                    }
                    this.buttonCallback[i] = button.callback;
                    buttonEl.addEventListener('click', button.callback);
                } else {
                    buttonEl.style.display = 'none';
                    this.buttonCallback[i] = null;
                }
            }
        }
        const modalCloseButton = document.querySelector<HTMLButtonElement>('#prompt-modal button.close-button');
        this.cancelButtonCallback = cancelCallBack;
        if (!this.cancelButtonCallback) {
            this.cancelButtonCallback = (): void => {
                this.hidePrompt();
            };
        }
        modalCloseButton.addEventListener('click', this.cancelButtonCallback);
        this.promptModal.style.display = 'flex';
    }

    private showOkPrompt(title: string, message: string, okButtonText: string, okButtonCallback: () => void = null): void {
        const buttons = [
            {
                text: okButtonText,
                callback: okButtonCallback,
                class: 'cta'
            }
        ];
        this.showPrompt(title, message, buttons, okButtonCallback);
    }

    private hideTermsShowControls(): void {
        // Give enough time for
        setTimeout(() => {
            document.getElementById('terms-and-conditions').style.display = 'none';
            this.previewContainerEl.style.pointerEvents = 'auto';
        }, 500);
    }

    private async userImageWarningSize(): Promise<boolean> {
        return new Promise((resolve: (dontUseImage: boolean) => void): void => {
            const title = SF.tr('WARNING_MEDIUM_QUALITY_IMAGE_TITLE', {}, 'Lower Quality Image', false);
            const message = SF.tr('WARNING_MEDIUM_QUALITY_IMAGE_MESSAGE', {}, 'The resolution of this image is poor. Using this image will produce a low quality product.  Are you sure you wish to continue?', false);
            const cancelCallback = () => {
                this.addAnalyticsEvent('warnImage', 'cancel');
                this.hidePrompt();
                resolve(true);
            };
            this.addAnalyticsEvent('warnImage', 'show-warning-modal');
            this.showPrompt(title, message, [
                {
                    text: SF.tr('BUTTON_NO', {}, 'No', false),
                    class: 'cta',
                    callback: cancelCallback
                },
                {
                    text: SF.tr('BUTTON_YES', {}, 'Yes', false),
                    callback: () => {
                        this.addAnalyticsEvent('warnImage', 'continue');
                        this.hidePrompt();
                        resolve(false);
                    }
                }
            ], cancelCallback);
        });
    }

    private userImageErrorSize(): void {
        const title = SF.tr('ERROR_LOW_QUALITY_IMAGE_TITLE', {}, 'Low Quality Image', false);
        const message = SF.tr('ERROR_LOW_QUALITY_IMAGE_MESSAGE', {}, 'This images resolution is too low to produce an acceptable product.  Please choose another image', false);
        this.showOkPrompt(title, message, SF.tr('BUTTON_OK', {}, 'OK', false));
    }

    private showTextPreviewError(): void {
        const title = SF.tr('ERROR_TOO_LARGE_TEXT', {}, 'Text Too Large', false);
        const message = SF.tr('TEXT_PREVIEW_ERROR', {}, 'One or more text elements are too large to render. Please reduce the font size and try again.', false);
        this.showOkPrompt(title, message, SF.tr('BUTTON_OK', {}, 'OK', false), () => {
            this.hidePrompt();
            this.reEnablePage();
            this.saveButton.classList.remove('is-loading');
            this.spinner.classList.remove('spin');
        });
    }

    // returns -1 if all pages are valid, otherwise the index of the first invalid page
    private async validateMultiPages(): Promise<number> {
        return new Promise((resolve: (invalidPage: number) => void): void => {
            if (this.pages.length === 1) {
                resolve(-1);
            } else {
                // Check all pages except the current page
                const foundInvalidPage = this.pages
                    .filter((_, idx) => idx !== this.currentPage)
                    .find(page =>
                        !page.edits.freeObject.every(freeObject =>
                            freeObject.isClipArt || !freeObject.imageEdits || freeObject.imageEdits.fullImageFilename
                        )
                    );
                resolve(foundInvalidPage ? this.pages.indexOf(foundInvalidPage) : -1);
            }
        });
    }

    private getText(textEdits: ITextEdits): string {
        if (textEdits.text) {
            return textEdits.text;
        }
        return this.getDefaultText(textEdits);
    }

    private getErrorObjects(elements: IEditorElement[],
                            elementTest: (el: IEditorElement) => boolean,
                            freeObjectTest: (freeObject: IFreeObject) => boolean): ITextError[] {
        let unchangedText = elements.filter(elementTest).map(el => {
            const freeObject = this.pages[this.currentPage].edits.freeObject.find(f => f.freeObjectId === el.freeObjectId);
            return {
                text: el.text,
                freeObjectId: el.freeObjectId,
                errorPage: this.currentPage,
                textZoneId: freeObject.textZoneId
            };
        });
        if (this.pages.length > 1) {
            for (let checkPage = 0; checkPage < this.pages.length; checkPage++) {
                if (this.currentPage !== checkPage) {
                    unchangedText = unchangedText.concat(this.pages[checkPage].edits.freeObject.filter(freeObjectTest).map(freeObject => {
                        return {
                            text: this.getText(freeObject.textEdits), freeObjectId: freeObject.freeObjectId,
                            errorPage: checkPage, textZoneId: freeObject.textZoneId
                        };
                    }));
                }
            }
        }
        return unchangedText;
    }

    private getDefaultText(textEdits: ITextEdits) {
        const compareText = textEdits.placeHolderTranslations?.[SF.currentLanguage] || '';
        if (!compareText || compareText.length === 0) {
            return SF.tr('PERSONALIZE', {}, 'Personalize', false);
        }
        return compareText;
    }


    private async validateText(): Promise<boolean> {
        return new Promise((resolve: (textOk: boolean) => void): void => {
            const elements: IEditorElement[] = this.editorController.getPageElements();

            const unchangedText = this.getErrorObjects(elements, el => {
                // Somewhere along the line el.edited is getting set to true without editing the product...
                return el instanceof SF.Model.TextElement && (el.editable || el.customerEditable) && !el.edited; // || this.isDefaultText(el.text, el.freeObjectId));
            }, freeObject => {
                return freeObject.textEdits && !freeObject.textEdits.edited && (freeObject.canEdit || freeObject.customerEditable);
            });
            if (unchangedText.length === 0) {
                const removedText = this.getErrorObjects(elements, el => {
                    return el instanceof SF.Model.TextElement && el.text.length === 0;
                }, freeObject => {
                    return freeObject.textEdits && freeObject.textEdits.text.length === 0;
                });
                if (removedText.length > 0) {
                    const title = SF.tr('REMOVED_TEXT_TITLE', {}, 'Text Fields Removed', false);
                    const message = SF.tr('REMOVED_TEXT_MESSAGE', {COUNT: removedText.length}, 'You have removed {{COUNT}} text fields.  Are you sure you wish to continue?', false);
                    const cancelCallback = () => {
                        this.addAnalyticsEvent('validateText', 'change-deleted-text');
                        this.reactivatePageAndHighlightText(unchangedText[0].errorPage);
                        resolve(false);
                    };
                    const buttons = [{
                        text: SF.tr('BUTTON_EDIT_TEXT', {}, 'Edit Text', false),
                        class: 'cta',
                        callback: () => {
                            cancelCallback();
                        }
                    },
                        {
                            text: SF.tr('BUTTON_SKIP_TEXT', {}, 'Continue with Missing Fields', false),
                            callback: async (): Promise<void> => {
                                resolve(true);
                            }
                        }
                    ];
                    this.showPrompt(title, message, buttons, cancelCallback);
                } else {
                    resolve(true);
                }
            } else {
                const title = SF.tr('UNCHANGED_TEXT_TITLE', {}, 'Personalization Needed', false);
                const noMessageUnchanged = unchangedText.filter(el => el.text.length > 1 && !el.text.match(/^20\d\d$/));
                const previewCallback = async () => {
                    this.addAnalyticsEvent('validateText', 'show-preview');
                    // hide prompt modal before fully loading the preview (PS-62502)
                    this.hidePrompt();
                    if (this.threeDModel) {
                        await this.showThreeDPreview();
                    } else {
                        await this.showItePreview();
                    }
                    this.reactivatePageAndHighlightText(unchangedText[0].errorPage).then(() => {
                        console.log('reactivatePageAndHighlightText done');
                    }, err => {
                        console.error('reactivatePageAndHighlightText error', err);
                    });
                    resolve(false);
                };
                if (this.forceTextChange && noMessageUnchanged.length > 0) {
                    this.addAnalyticsEvent('validateText', 'show-message-require');
                    const message = SF.tr('UNCHANGED_TEXT_MESSAGE_FORCE', {TEXT: noMessageUnchanged[0].text}, '"{{TEXT}}" is unchanged.  You must change the text to continue', false);
                    const callback = (): void => {
                        this.addAnalyticsEvent('validateText', 'show-message-require-ok');
                        this.reactivatePageAndHighlightText(noMessageUnchanged[0].errorPage);
                        resolve(false);
                    };
                    if ((this.threeDModel || this.itePreview) && !this.showPreviewOnSave() && this.showPreviewInForceModal) {
                        const buttons = [
                            {
                                text: SF.tr('BUTTON_SHOW_PREVIEW', {}, 'Preview Your Product', false),
                                class: 'cta',
                                callback: previewCallback
                            },
                            {
                                text: SF.tr('BUTTON_OK', {}, 'OK', false),
                                callback: callback
                            }
                        ];
                        this.showPrompt(title, message, buttons, callback);
                    } else {
                        this.showOkPrompt(title, message, SF.tr('BUTTON_OK', {}, 'OK', false), callback);
                    }

                } else {
                    this.addAnalyticsEvent('validateText', 'show-message-options');

                    let message: string;
                    let okButtonText: string;
                    if (noMessageUnchanged.length === 0 && unchangedText.filter(el => el.text.length > 1 && !el.text.match(/^20\d\d$/)).length > 0) {
                        resolve(true);
                        return;
                    } else {
                        message = SF.tr('UNCHANGED_TEXT_MESSAGE', {TEXT: unchangedText[0].text}, 'Currently the default text, "{{TEXT}}" will print on your Item.\n\nHow do you wish to proceed?', false);
                        okButtonText = SF.tr('BUTTON_PRINT_DEFAULT', {}, 'Print Default Text', false);
                    }
                    const cancelCallback = (): void => {
                        this.addAnalyticsEvent('validateText', 'show-message-fix');
                        this.reactivatePageAndHighlightText(unchangedText[0].errorPage);
                        resolve(false);
                    };

                    const buttons = [];
                    if (this.showPreviewInForceModal && (this.threeDModel || this.itePreview)) {
                        buttons.push({
                            text: SF.tr('BUTTON_SHOW_PREVIEW', {}, 'Preview Your Product', false),
                            class: 'cta',
                            callback: () => {
                                this.addAnalyticsEvent('validateText', 'show-message-preview');
                                previewCallback();
                            }
                        });
                    }
                    buttons.push({
                        text: SF.tr('BUTTON_EDIT_TEXT', {}, 'Edit Text', false),
                        class: this.showPreviewInForceModal && this.threeDModel ? '' : 'cta',
                        callback: () => {
                            this.addAnalyticsEvent('validateText', 'show-message-edit');
                            cancelCallback();
                        }
                    });
                    buttons.push(
                        {
                            text: okButtonText,
                            class: '',
                            callback: async (): Promise<void> => {
                                unchangedText.forEach(unchanged => {
                                    const freeObject = this.pages[unchanged.errorPage].edits.freeObject.find(searchFreeObject =>
                                        searchFreeObject.freeObjectId === unchanged.freeObjectId);
                                    if (freeObject) {
                                        freeObject.textEdits.edited = true;
                                    } else {
                                        console.error(`Unable to find freeObject with id ${unchanged.freeObjectId} on page ${unchanged.errorPage}`);
                                    }
                                });
                                this.addAnalyticsEvent('validateText', 'show-message-print-default');
                                this.hidePrompt();
                                for (let i = 0; i < this.pages.length; i++) {
                                    if (i !== this.currentPage) {
                                        const pageHasUnchangedText = this.pages[i].edits.freeObject.find((object: IFreeObject) => {
                                            return object.textEdits && !object.textEdits.edited && !(object.textEdits as any).preview_image;
                                        });
                                        if (pageHasUnchangedText) {
                                            await this.editorController.captureDefaultTextPreview(this.pages[i]);
                                        }
                                    }

                                }
                                resolve(true);
                            }
                        }
                    );
                    this.showPrompt(title, message, buttons, cancelCallback);
                }
            }
        });
    }

    private async reactivatePageAndHighlightText(errorPage: number): Promise<void> {
        console.log('page ' + errorPage);
        this.reEnablePage();
        try {
            if (errorPage !== this.currentPage) {
                const pageTab = document.querySelector<HTMLLIElement>('#page-tabs li[data-page="' + errorPage + '"]');
                if (pageTab) {
                    await this.changePageClick(pageTab, true);
                }
            }
            this.hidePrompt();
        } catch (err) {
            console.error(err);
        }
    }

    private reEnablePage(): void {
        this.previewContainerEl.style.pointerEvents = 'auto';
        document.querySelectorAll<HTMLLIElement>('#edit-page-tabs li').forEach(li => {
            li.style.pointerEvents = 'auto';
        });
        document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('#edit-page-content input, #edit-page-content select').forEach(el => {
            el.disabled = false;
        });
        this.changeEdits(true);
        document.querySelector<HTMLButtonElement>('#button-cancel').disabled = false;
        if (this.zoom) {
            this.updateZoom(this.zoom);
        }
    }

    private getPages(pageList: IPageList[]): IPage[] {
        return pageList.map(page => {
            return {
                edits: page.edits,
                page_id: page.page_id,
                page_number: page.page_number,
                preview_image: page.preview_image,
                elements: [],
                project_id: this.projectId
            };
        });
    }

    private fixFreeObjectIds(page: IPageList | IPage): void {
        for (let i = 0; i < page.edits.freeObject.length; i++) {
            if (page.edits.freeObject[i].freeObjectId === undefined) {
                page.edits.freeObject[i].freeObjectId = this.uniqueFreeObjectId;
                this.uniqueFreeObjectId += 1;
            }
        }
    }

    private fixPageForFreeObjects(page: IPageList, alreadyEdited: boolean) {
        this.fixFreeObjectIds(page);
        for (let i = 0; i < page.edits.freeObject.length; i++) {

            // You can't edit clipart in the Customizer.  Turn that off.
            if (page.edits.freeObject[i].isClipArt && page.edits.freeObject[i].canEdit) {
                page.edits.freeObject[i].canEdit = false;
            }

            // Fix any text with placeholders that don't match the rules associated with it.
            if (page.edits.freeObject[i].textEdits) {
                const textEdits = page.edits.freeObject[i].textEdits;
                // When coming from Printsuite, textEdits.edited is already set...
                if (!alreadyEdited && textEdits.edited) {
                    textEdits.edited = false;
                } else {
                    Object.keys(textEdits?.placeHolderTranslations || []).forEach(language => {
                        textEdits.placeHolderTranslations[language] = this.fixTextFromOptions(textEdits.placeHolderTranslations[language], textEdits);
                    });
                }
            }
        }
    }

    private isReEditOrTextOnly(pageList: IPageList[]): boolean {

        return pageList.every(page => {
            return page.edits.freeObject.every(freeObject => {
                if (this.alwaysShowTermsAndConditions) {
                    if (CustomizerImagingUtils.isPhotoZone(freeObject)) {
                        return freeObject.imageEdits && freeObject.imageEdits.fullImageFilename;
                    } else if (freeObject.textEdits) {
                        return freeObject.textEdits.edited;
                    }
                    return true;
                } else {
                    return !CustomizerImagingUtils.isPhotoZone(freeObject) || (freeObject.imageEdits && freeObject.imageEdits.fullImageFilename);
                }
            });
        });

    }

    private getPreviewFromPage(page: IPage): Promise<string> {
        return new Promise<string>((resolve: (image: string) => void, reject: (error: string) => void): void => {
            if (page.preview_image) {
                resolve(page.preview_image);
            } else {
                // Create temporary preview texts that have the placeHolder and are set to edited so that they show up.
                const previewPage = Object.assign({}, page);
                previewPage.edits.freeObject.forEach(freeObject => {
                    if (freeObject.textEdits && !freeObject.textEdits.text) {
                        freeObject.textEdits.text = this.getText(freeObject.textEdits);
                        freeObject.textEdits.edited = freeObject.textEdits.edited || false;
                    }
                });
                this.editorController.getSnapShotsFromAllPages([previewPage], this.productImageWidth,
                    false, true, 'png').then((previews: string[]): void => {
                    resolve(previews[0]);
                }, err => {
                    reject(err);
                });
            }
        });
    }

    private blendOverlay(canvas: HTMLCanvasElement, blendOverlay: {
        url?: string,
        blend?: GlobalCompositeOperation
    }): Promise<HTMLCanvasElement> {
        return new Promise<HTMLCanvasElement>(async (resolve: (canvas: HTMLCanvasElement) => void,
                                                     reject: (err: string) => void): Promise<void> => {
            const newCanvas = document.createElement('canvas');
            newCanvas.width = canvas.width;
            newCanvas.height = canvas.height;
            let overlay: HTMLImageElement;
            try {
                overlay = await CustomizerImagingUtils.getImageFromUrl(blendOverlay.url);
            } catch (err) {
                reject(err);
            }
            const ctx = newCanvas.getContext('2d');
            ctx.drawImage(canvas, 0, 0);
            ctx.globalCompositeOperation = blendOverlay.blend || 'source-over';
            ctx.drawImage(overlay, 0, 0, newCanvas.width, newCanvas.height);
            resolve(newCanvas);
        });
    }

    private showPreviewOnSave(): boolean {
        const isMobile = window.matchMedia('only screen and (max-width: 760px)').matches;
        return (this.previewOnSave === SHOW_PREVIEW_ALWAYS ||
            (this.previewOnSave === SHOW_PREVIEW_ON_MOBILE && isMobile));
    }

    private getSizeFromWidth(width: number): string {
        if (width < 160) {
            return 'xxxsmall';
        } else if (width < 180) {
            return 'xxsmall';
        } else if (width < 200) {
            return 'xsmall';
        } else if (width < 220) {
            return 'small';
        }
        return '';
    }

    private applyStyle(el: HTMLElement, style: { [key: string]: string }): void {
        Object.keys(style).forEach(key => el.style[key as any] = style[key]);
    }


}


